import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { User, Utensils, Stethoscope, UserCog, Settings, CalendarClock, FileText, ChevronDown } from 'lucide-react';
import {
  nutritionProfileService,
  derivedMetricsService,
  NutritionProfile,
  NutritionProfileCreateInput,
  NutritionProfileUpdateInput,
  NutritionProfileParseResult,
  NutritionProfileExtractionItem,
  DerivedMetricsSummaryResponse,
  DerivedMetricSummaryItem,
  PreferencesJSON,
  HumanContextJSON,
  ConstraintsJSON,
  MedicalHistoryJSON,
  MealPatternJSON,
} from '../../services/nutrition/nutritionProfileService';
import { VITAL_BACKED_DERIVED_METRICS } from '../../services/nutrition/derivedMetricsVitals';
import { nutritionDictionaryService, DictionaryItem } from '../../services/dictionaries/nutritionDictionaryService';
import { DEFAULT_MEAL_TYPE_ORDER, DEFAULT_MEAL_TIMES } from '../../services/nutrition/mealTypeConstants';
import DateOfBirthPicker from '../../components/DateOfBirthPicker';
import { HealthVitalsInsightsView } from '../../components/health/HealthVitalsInsightsView';

interface PatientNutritionProfileTabProps {
  patientId: number;
}

type StepId =
  | 'import_or_manual'
  | 'basics'
  | 'preferences'
  | 'medical_history'
  | 'human_context'
  | 'constraints'
  | 'meal_pattern'
  | 'insights'
  | 'review';

/** Dictionary keys to load when entering each step (lazy load per step instead of all on tab load). */
const STEP_DICTIONARY_KEYS: Record<StepId, string[]> = {
  import_or_manual: [],
  basics: ['capturedVia', 'maritalStatus', 'activityLevel', 'motivationReadiness', 'technologyComfortLevel'],
  preferences: ['dietType', 'allergyType', 'foodIntolerance', 'nutritionalDeficiency', 'waterIntake', 'snackFrequency', 'weekendPattern', 'mealType'],
  medical_history: ['diseases', 'medication', 'personalHistory', 'familyHistory'],
  human_context: ['sleepQuality', 'sleepPattern', 'workSchedule', 'stressLevel', 'familyStructure', 'cookingResponsibility', 'alcoholConsumption', 'religiousRestriction', 'smokingStatus', 'cultureIdentity'],
  constraints: [],
  meal_pattern: ['mealType', 'snackFrequency', 'weekendPattern'],
  insights: [],
  review: [],
};

const NUTRITION_PROFILE_DRAFT_KEY = (patientId: number) => `zivo_nutrition_profile_draft_${patientId}`;

/** Persisted draft state for nutrition profile (survives refresh/network loss). */
interface NutritionProfileDraft {
  activeStep: StepId;
  form: NutritionProfileCreateInput;
  parseResult: NutritionProfileParseResult | null;
  parseResultsByDocument: NutritionProfileExtractionItem[] | null;
  selectedDocumentPerField: Record<string, number>;
  insightsEdits: Record<string, number>;
  savedAt: string;
}

function loadNutritionProfileDraft(patientId: number): NutritionProfileDraft | null {
  try {
    const raw = localStorage.getItem(NUTRITION_PROFILE_DRAFT_KEY(patientId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NutritionProfileDraft;
    if (!parsed || typeof parsed.activeStep !== 'string' || !parsed.form) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveNutritionProfileDraft(patientId: number, draft: Omit<NutritionProfileDraft, 'savedAt'>): void {
  try {
    const payload: NutritionProfileDraft = { ...draft, savedAt: new Date().toISOString() };
    localStorage.setItem(NUTRITION_PROFILE_DRAFT_KEY(patientId), JSON.stringify(payload));
  } catch {
    // ignore quota / serialization errors
  }
}

function clearNutritionProfileDraft(patientId: number): void {
  try {
    localStorage.removeItem(NUTRITION_PROFILE_DRAFT_KEY(patientId));
  } catch {
    // ignore
  }
}

const steps: { id: StepId; title: string; subtitle: string }[] = [
  {
    id: 'import_or_manual',
    title: 'Import or enter data',
    subtitle: 'Upload documents to extract values, or enter manually.',
  },
  {
    id: 'basics',
    title: 'Basics',
    subtitle: 'Snapshot weight/activity + notes (versioned).',
  },
  {
    id: 'preferences',
    title: 'Food preferences',
    subtitle: 'Diet types, allergies, intolerances, nutritional info.',
  },
  {
    id: 'medical_history',
    title: 'Medical history',
    subtitle: 'Diseases, medication, personal and family history.',
  },
  {
    id: 'human_context',
    title: 'Lifestyle',
    subtitle: 'Culture, religion, work/sleep/stress, cooking responsibility.',
  },
  {
    id: 'constraints',
    title: 'Constraints',
    subtitle: 'Cooking, budget, schedule, and "won\'t do" items.',
  },
  {
    id: 'meal_pattern',
    title: 'Meal pattern',
    subtitle: 'Meals per day, timings, snacks, weekend patterns.',
  },
  {
    id: 'insights',
    title: 'Insights',
    subtitle: 'Derived metrics and composite scores from vitals and profile.',
  },
  {
    id: 'review',
    title: 'Review & Save',
    subtitle: 'Confirm details and save (creates new version on update).',
  },
];

/** Display labels for extracted (snake_case) keys in the preview modal */
const EXTRACTED_FIELD_LABELS: Record<string, string> = {
  gender: 'Gender',
  date_of_birth: 'Date of birth',
  height_cm: 'Height (cm)',
  body_weight_kg: 'Body weight (kg)',
  waist_cm: 'Waist circumference (cm)',
  hip_cm: 'Hip circumference (cm)',
  neck_cm: 'Neck circumference (cm)',
  bmi: 'BMI',
  bsa: 'Body surface area (m²)',
  body_fat_pct: 'Body fat (%)',
  fat_free_body_weight_kg: 'Fat-free body weight (kg)',
  fat_mass_kg: 'Fat mass (kg)',
  subcutaneous_fat_pct: 'Subcutaneous fat (%)',
  visceral_fat_grade: 'Visceral fat grade',
  body_water_pct: 'Body water (%)',
  body_water_kg: 'Body water (kg)',
  intracellular_water_kg: 'Intracellular water (kg)',
  extracellular_water_kg: 'Extracellular water (kg)',
  ecw_tbw_ratio: 'ECW/TBW ratio',
  mineral_mass_kg: 'Mineral mass (kg)',
  phase_angle: 'Phase angle (°)',
  skeletal_muscle_pct: 'Skeletal muscle (%)',
  skeletal_muscle_kg: 'Skeletal muscle (kg)',
  muscle_mass_kg: 'Muscle mass (kg)',
  bone_mass_kg: 'Bone mass (kg)',
  protein_pct: 'Protein (%)',
  protein_mass_kg: 'Protein mass (kg)',
  bmr_kcal: 'BMR (kcal)',
  metabolic_age: 'Metabolic age',
  body_age: 'Body age',
  body_score: 'Body score',
  whr: 'Waist-to-hip ratio',
  smi_kg_m2: 'Skeletal muscle index (kg/m²)',
  obesity_pct: 'Obesity (%)',
  body_type: 'Body type',
  bmi_evaluation: 'BMI evaluation',
  is_pregnant: 'Pregnant',
  is_lactating: 'Lactating',
  life_stage: 'Life stage',
  types_of_diet: 'Types of diet',
  diet_notes: 'Diet notes',
  favorite_food: 'Favorite food',
  disliked_food: 'Disliked food',
  allergies: 'Allergies',
  allergies_notes: 'Allergies notes',
  food_intolerances: 'Food intolerances',
  food_intolerances_notes: 'Intolerances notes',
  nutritional_deficiencies: 'Nutritional deficiencies',
  nutritional_deficiencies_notes: 'Deficiencies notes',
  water_intake: 'Water intake',
  preferences_other: 'Preferences other',
  mother_tongue: 'Mother tongue',
  marital_status: 'Marital status',
  motivation_readiness: 'Motivation readiness',
  technology_comfort_level: 'Technology comfort level',
  previous_diet_attempts: 'Previous diet attempts',
  support_system_available: 'Support system available',
  activity_level: 'Activity level',
  culture_identity: 'Culture / identity',
  religious_restrictions: 'Religious restrictions',
  sleep_patterns: 'Sleep patterns',
  stress_levels: 'Stress levels',
  work_schedule: 'Work schedule',
  family_structure: 'Family structure',
  cooking_responsibility: 'Cooking responsibility',
  sleep_quality: 'Sleep quality',
  smoking_status: 'Smoking status',
  alcohol_consumption: 'Alcohol consumption',
  diseases: 'Diseases',
  diseases_notes: 'Diseases notes',
  medication: 'Medication',
  medication_notes: 'Medication notes',
  personal_history: 'Personal history',
  personal_history_notes: 'Personal history notes',
  family_history: 'Family history',
  family_history_notes: 'Family history notes',
  medical_history_other: 'Medical history other',
  cooking_skills_level: 'Cooking skills level',
  monthly_budget: 'Monthly budget',
  budget_currency: 'Currency',
  work_hours: 'Work hours',
  meal_prep_time_min: 'Meal prep time (min)',
  eating_out_frequency: 'Eating out frequency',
  wont_do: "Won't do",
  typical_meals_per_day: 'Meals per day',
  snack_frequency: 'Snack frequency',
  weekend_patterns: 'Weekend patterns',
  late_night_eating: 'Late night eating',
  meal_times_notes: 'Meal times notes',
  doctor_notes: 'Doctor notes',
  patient_notes: 'Patient notes',
};

/** Merge per-document extractions into one result using selected document per field (default first document). */
function mergeExtractions(
  extractions: NutritionProfileExtractionItem[],
  selectedDocPerField: Record<string, number>,
): NutritionProfileParseResult {
  const merged: Record<string, unknown> = {};
  const allKeys = new Set<string>();
  extractions.forEach(({ extracted }) => Object.keys(extracted || {}).forEach((k) => allKeys.add(k)));
  allKeys.forEach((key) => {
    const docIndex = selectedDocPerField[key] ?? 0;
    const doc = extractions[docIndex];
    if (doc?.extracted) {
      const v = (doc.extracted as Record<string, unknown>)[key];
      if (v !== undefined && v !== null && v !== '') (merged[key] = v);
    }
  });
  return merged as NutritionProfileParseResult;
}

/** Get parsed document value for a derived metric key (used for Insights and save-to-vitals). Must cover all VITAL_BACKED_DERIVED_METRICS and composite score keys that can come from parseResult. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for save-to-vitals / future use
function getParsedValueForMetric(p: NutritionProfileParseResult | null, metricKey: string): number | null {
  if (!p) return null;
  const v = (() => {
    switch (metricKey) {
      case 'body_weight_kg': return p.body_weight_kg ?? null;
      case 'bmi': return p.bmi ?? (p.body_weight_kg != null && p.height_cm != null && p.height_cm > 0 ? p.body_weight_kg / ((p.height_cm / 100) ** 2) : null);
      case 'bsa': return p.bsa ?? (p.height_cm != null && p.body_weight_kg != null && p.height_cm > 0 && p.body_weight_kg > 0 ? 0.007184 * (p.height_cm ** 0.725) * (p.body_weight_kg ** 0.425) : null);
      case 'body_fat_pct': return p.body_fat_pct ?? null;
      case 'body_fat_mass_kg': return p.fat_mass_kg ?? (p.body_weight_kg != null && p.body_fat_pct != null ? (p.body_weight_kg * p.body_fat_pct) / 100 : null);
      case 'fat_free_mass_kg': return p.fat_free_body_weight_kg ?? null;
      case 'skeletal_muscle_kg': return p.skeletal_muscle_kg ?? null;
      case 'skeletal_muscle_pct': return p.skeletal_muscle_pct ?? null;
      case 'protein_mass_kg': return p.protein_mass_kg ?? null;
      case 'protein_pct': return p.protein_pct ?? null;
      case 'body_water_kg': return p.body_water_kg ?? null;
      case 'body_water_pct': return p.body_water_pct ?? null;
      case 'intracellular_water_kg': return p.intracellular_water_kg ?? null;
      case 'extracellular_water_kg': return p.extracellular_water_kg ?? null;
      case 'ecw_tbw_ratio': return p.ecw_tbw_ratio ?? null;
      case 'mineral_mass_kg': return p.mineral_mass_kg ?? null;
      case 'bone_mass_kg': return p.bone_mass_kg ?? null;
      case 'phase_angle': return p.phase_angle ?? null;
      case 'visceral_fat_level': return p.visceral_fat_grade ?? null;
      case 'basal_metabolic_rate_kcal': return p.bmr_kcal ?? null;
      case 'body_age': return p.body_age ?? p.metabolic_age ?? null;
      case 'body_score': return p.body_score ?? null;
      case 'whtr': return (p.waist_cm != null && p.height_cm != null && p.height_cm > 0) ? p.waist_cm / p.height_cm : null;
      case 'whr': return p.whr ?? (p.waist_cm != null && p.hip_cm != null && p.hip_cm > 0 ? p.waist_cm / p.hip_cm : null);
      case 'bai': return (p.hip_cm != null && p.height_cm != null && p.height_cm > 0) ? (p.hip_cm / ((p.height_cm / 100) ** 1.5)) - 18 : null;
      case 'bfp': return p.body_fat_pct ?? null;
      case 'lbm': return p.fat_free_body_weight_kg ?? null;
      case 'fm': return p.fat_mass_kg ?? (p.body_weight_kg != null && p.body_fat_pct != null ? (p.body_weight_kg * p.body_fat_pct) / 100 : null);
      case 'fat_free_mass': return p.fat_free_body_weight_kg ?? null;
      case 'bmr': return p.bmr_kcal ?? null;
      case 'metabolic_age': return p.metabolic_age ?? p.body_age ?? null;
      case 'vfi': return p.visceral_fat_grade ?? null;
      case 'smi': return p.smi_kg_m2 ?? null;
      case 'tbw': return p.body_water_kg ?? null;
      case 'bone_mass': return p.bone_mass_kg ?? null;
      case 'muscle_mass_kg': return p.muscle_mass_kg ?? p.skeletal_muscle_kg ?? null;
      case 'subcutaneous_fat_pct': return p.subcutaneous_fat_pct ?? null;
      case 'obesity_pct': return p.obesity_pct ?? null;
      default: return null;
    }
  })();
  return v != null && typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Form key -> parsed value (from parseResult). Used for color coding and revert on all steps. */
function getParsedValueForFormKey(p: NutritionProfileParseResult | null, formKey: string): unknown {
  if (!p) return undefined;
  switch (formKey) {
    case 'gender': return p.gender ?? undefined;
    case 'dateOfBirth': return p.date_of_birth ?? undefined;
    case 'heightCm': return p.height_cm ?? undefined;
    case 'bodyWeightKg': return p.body_weight_kg ?? undefined;
    case 'waistCm': return p.waist_cm ?? undefined;
    case 'hipCm': return p.hip_cm ?? undefined;
    case 'neckCm': return p.neck_cm ?? undefined;
    case 'isPregnant': return p.is_pregnant ?? undefined;
    case 'isLactating': return p.is_lactating ?? undefined;
    case 'lifeStage': return p.life_stage ?? undefined;
    case 'motherTongue': return p.mother_tongue ?? undefined;
    case 'activityLevel': return p.activity_level ?? undefined;
    case 'motivationReadiness': return p.motivation_readiness ?? undefined;
    case 'technologyComfortLevel': return p.technology_comfort_level ?? undefined;
    case 'previousDietAttempts': return p.previous_diet_attempts ?? undefined;
    case 'supportSystemAvailable': return p.support_system_available ?? undefined;
    case 'doctorNotes': return p.doctor_notes ?? undefined;
    case 'patientNotes': return p.patient_notes ?? undefined;
    case 'maritalStatus': return p.marital_status ?? undefined;
    case 'typesOfDiet': return p.types_of_diet ?? undefined;
    case 'typesOfDietNotes': return p.diet_notes ?? undefined;
    case 'favoriteFood': return p.favorite_food ?? undefined;
    case 'dislikedFood': return p.disliked_food ?? undefined;
    case 'allergies': return p.allergies ?? undefined;
    case 'allergiesNotes': return p.allergies_notes ?? undefined;
    case 'foodIntolerances': return p.food_intolerances ?? undefined;
    case 'foodIntolerancesNotes': return p.food_intolerances_notes ?? undefined;
    case 'nutritionalDeficiencies': return p.nutritional_deficiencies ?? undefined;
    case 'nutritionalDeficienciesNotes': return p.nutritional_deficiencies_notes ?? undefined;
    case 'waterIntake': return p.water_intake ?? undefined;
    case 'otherInformation': return p.preferences_other ?? undefined;
    case 'cultureIdentity': return p.culture_identity ?? undefined;
    case 'religiousRestrictions': return p.religious_restrictions ?? undefined;
    case 'sleepPatterns': return p.sleep_patterns ?? undefined;
    case 'stressLevels': return p.stress_levels ?? undefined;
    case 'workSchedule': return p.work_schedule ?? undefined;
    case 'familyStructure': return p.family_structure ?? undefined;
    case 'cookingResponsibility': return p.cooking_responsibility ?? undefined;
    case 'sleepQuality': return p.sleep_quality ?? undefined;
    case 'smokingStatus': return p.smoking_status ?? undefined;
    case 'alcoholConsumption': return p.alcohol_consumption ?? undefined;
    case 'diseases': return p.diseases ?? undefined;
    case 'diseasesNotes': return p.diseases_notes ?? undefined;
    case 'medication': return p.medication ?? undefined;
    case 'medicationNotes': return p.medication_notes ?? undefined;
    case 'personalHistory': return p.personal_history ?? undefined;
    case 'personalHistoryNotes': return p.personal_history_notes ?? undefined;
    case 'familyHistory': return p.family_history ?? undefined;
    case 'familyHistoryNotes': return p.family_history_notes ?? undefined;
    case 'medicalHistoryOther': return p.medical_history_other ?? undefined;
    case 'cookingSkillsLevel': return p.cooking_skills_level ?? undefined;
    case 'monthlyBudget': return p.monthly_budget ?? undefined;
    case 'budgetCurrency': return p.budget_currency ?? undefined;
    case 'workHours': return p.work_hours ?? undefined;
    case 'mealPrepTimeMin': return p.meal_prep_time_min ?? undefined;
    case 'eatingOutFrequency': return p.eating_out_frequency ?? undefined;
    case 'wontDo': return p.wont_do ?? undefined;
    case 'typicalMealsPerDay': return p.typical_meals_per_day ?? undefined;
    case 'snackFrequency': return p.snack_frequency ?? undefined;
    case 'weekendPatterns': return p.weekend_patterns ?? undefined;
    case 'lateNightEating': return p.late_night_eating ?? undefined;
    default: return undefined;
  }
}

/** Compare current value with parsed; normalize for equality (e.g. number vs string). */
function parsedValueEquals(parsed: unknown, current: unknown): boolean {
  if (parsed === current) return true;
  if (parsed == null && (current === '' || current == null)) return true;
  if (typeof parsed === 'number' && (current === parsed || current === String(parsed))) return true;
  if (typeof parsed === 'string' && (current === parsed || (typeof current === 'number' && String(current) === parsed))) return true;
  if (typeof parsed === 'boolean' && (current === parsed || (current === (parsed ? 'yes' : 'no')))) return true;
  return false;
}

/** Source for a form field: from_document | edited | user_data. */
function getSourceForFormField(
  p: NutritionProfileParseResult | null,
  formKey: string,
  currentValue: unknown,
): 'from_document' | 'edited' | 'user_data' {
  const parsedVal = getParsedValueForFormKey(p, formKey);
  if (parsedVal === undefined || parsedVal === null) return 'user_data';
  if (parsedValueEquals(parsedVal, currentValue)) return 'from_document';
  return 'edited';
}

/** Inline styles for input/select when value is from document or edited (color in box only). */
function getParsedFieldInputStyle(source: 'from_document' | 'edited' | 'user_data', existingStyle?: React.CSSProperties): React.CSSProperties {
  const base = { ...existingStyle };
  if (source === 'from_document') {
    return { ...base, border: '1px solid #22c55e', backgroundColor: '#dcfce7' };
  }
  if (source === 'edited') {
    return { ...base, border: '1px solid #eab308', backgroundColor: '#fef9c3' };
  }
  return base;
}

/** Class names so CSS can apply full background (entire message box) over wizard's white !important. */
function getParsedFieldInputClassName(source: 'from_document' | 'edited' | 'user_data'): string {
  if (source === 'from_document') return 'np-input-from-document';
  if (source === 'edited') return 'np-input-edited';
  return '';
}

/** Legend shown when parseResult exists (for Basics, Preferences, etc.). */
function ParsedLegend() {
  return (
    <div style={{ display: 'flex', gap: '24px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '600' }}>Legend:</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem' }}>
        <span style={{ width: '14px', height: '14px', borderRadius: '2px', backgroundColor: '#dcfce7', border: '1px solid #22c55e' }} />
        <span style={{ color: '#166534', fontWeight: '500' }}>From document</span>
        <span style={{ color: '#6b7280' }}>— loaded from parsed file</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem' }}>
        <span style={{ width: '14px', height: '14px', borderRadius: '2px', backgroundColor: '#fef9c3', border: '1px solid #eab308' }} />
        <span style={{ color: '#854d0e', fontWeight: '500' }}>Edited</span>
        <span style={{ color: '#6b7280' }}>— Revert to load original value</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem' }}>
        <span style={{ width: '14px', height: '14px', borderRadius: '2px', backgroundColor: '#f3f4f6', border: '1px solid #9ca3af' }} />
        <span style={{ color: '#374151' }}>User data</span>
        <span style={{ color: '#6b7280' }}>— from profile</span>
      </span>
    </div>
  );
}

function buildTimeOptions(stepMinutes: number): Array<{ value: string; label: string }> {
  const opts: Array<{ value: string; label: string }> = [];
  const step = Math.max(1, Math.min(60, Math.floor(stepMinutes)));
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const value = `${hh}:${mm}`;
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${String(hour12).padStart(2, '0')}:${mm} ${ampm}`;
      opts.push({ value, label });
    }
  }
  return opts;
}

// Helper functions to convert between HH:MM format and separate hour/minute/ampm
function parseTimeToComponents(timeStr: string | null | undefined): { hour: string; minute: string; ampm: string } {
  if (!timeStr || !timeStr.includes(':')) {
    return { hour: '', minute: '', ampm: '' };
  }
  const [hh, mm] = timeStr.split(':');
  const hour24 = parseInt(hh, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 < 12 ? 'AM' : 'PM';
  return {
    hour: String(hour12),
    minute: mm || '',
    ampm: ampm,
  };
}

function combineTimeComponents(hour: string, minute: string, ampm: string): string {
  if (!hour || !minute || !ampm) {
    return '';
  }
  const hour12 = parseInt(hour, 10);
  let hour24 = hour12;
  if (ampm === 'AM' && hour12 === 12) {
    hour24 = 0;
  } else if (ampm === 'PM' && hour12 !== 12) {
    hour24 = hour12 + 12;
  }
  return `${String(hour24).padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

const emptyPreferences = (): PreferencesJSON => ({
  typesOfDiet: null,
  typesOfDietNotes: null,
  favoriteFood: null,
  dislikedFood: null,
  allergies: null,
  allergiesNotes: null,
  foodIntolerances: null,
  foodIntolerancesNotes: null,
  nutritionalDeficiencies: null,
  nutritionalDeficienciesNotes: null,
  waterIntake: null,
  otherInformation: null,
});

const emptyHumanContext = (): HumanContextJSON => ({
  activityLevel: 'moderate',
  cultureIdentity: '',
  religiousRestrictions: [],
  sleepPatterns: '',
  stressLevels: '',
  workSchedule: '',
  familyStructure: '',
  cookingResponsibility: '',
  sleepQuality: null,
  smokingStatus: null,
  alcoholConsumption: null,
  maritalStatus: null,
});

const emptyMedicalHistory = (): MedicalHistoryJSON => ({
  diseases: null,
  diseasesNotes: null,
  medication: null,
  medicationNotes: null,
  personalHistory: null,
  personalHistoryNotes: null,
  familyHistory: null,
  familyHistoryNotes: null,
  otherInformation: null,
});

const emptyConstraints = (): ConstraintsJSON => ({
  cooking: {
    has_stove: true,
    has_microwave: true,
    has_refrigerator: true,
    has_oven: false,
  },
  budget: {
    monthly_budget: undefined,
    currency: 'INR',
  },
  schedule: {
    work_hours: '',
    meal_prep_time_min: 30,
  },
  wontDo: [],
});

const emptyMealPattern = (): MealPatternJSON => ({
  typicalMealsPerDay: 3,
  mealTimes: {},
  snackFrequency: 'sometimes',
  lateNightEating: false,
  weekendPatterns: 'same_as_weekday',
});

// Available meal types (fallback when API dictionary not loaded)
const MEAL_TYPES = [
  { value: 'early_morning', label: 'Early Morning' },
  { value: 'pre_breakfast', label: 'Pre Breakfast' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'post_breakfast', label: 'Post Breakfast' },
  { value: 'brunch', label: 'Brunch' },
  { value: 'pre_lunch', label: 'Pre Lunch' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'post_lunch', label: 'Post Lunch' },
  { value: 'snack', label: 'Snack' },
  { value: 'evening_snack', label: 'Evening Snack' },
  { value: 'tea', label: 'Tea' },
  { value: 'high_tea', label: 'High Tea' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'post_dinner', label: 'Post Dinner' },
  { value: 'supper', label: 'Supper' },
  { value: 'midnight_snack', label: 'Midnight Snack' },
] as const;

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

// Helper function to derive life stage from date of birth
function deriveLifeStageFromDOB(dateOfBirth?: string): string | undefined {
  if (!dateOfBirth) return undefined;
  
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  
  if (age < 2) return 'infant';
  if (age < 13) return 'child';
  if (age < 18) return 'adolescent';
  if (age < 65) return 'adult';
  return 'elderly';
}

function joinCsv(values?: string[]): string {
  return (values || []).join(', ');
}

// Validation functions for each step
interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

function validateBasicsStep(form: NutritionProfileCreateInput): ValidationResult {
  const errors: string[] = [];
  
  if (!form.gender) {
    errors.push('Gender is required');
  }
  if (!form.dateOfBirth) {
    errors.push('Date of Birth is required');
  }
  if (!form.heightCm || form.heightCm <= 0) {
    errors.push('Height (cm) is required and must be greater than 0');
  }
  if (!form.bodyWeightKg || form.bodyWeightKg <= 0) {
    errors.push('Body weight (kg) is required and must be greater than 0');
  }
  // Waist, hip, neck circumference are optional in doctor portal
  if (!form.motherTongue) {
    errors.push('Mother tongue is required');
  }
  if (!form.humanContextJson?.maritalStatus) {
    errors.push('Marital status is required');
  }
  if (!form.motivationReadiness) {
    errors.push('Motivation readiness is required');
  }
  if (!form.technologyComfortLevel) {
    errors.push('Technology comfort level is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validatePreferencesStep(form: NutritionProfileCreateInput): ValidationResult {
  const errors: string[] = [];
  const preferences = form.preferencesJson;
  
  if (!preferences?.typesOfDiet) {
    errors.push('Types of diet is required');
  }
  if (preferences?.typesOfDiet === 'other' && !preferences?.typesOfDietNotes) {
    errors.push('Types of diet notes is required when "Other" is selected');
  }
  if (!preferences?.waterIntake) {
    errors.push('Water intake is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateMedicalHistoryStep(form: NutritionProfileCreateInput): ValidationResult {
  // Medical history is optional, but we can add validation if needed
  return { isValid: true, errors: [] };
}

function validateHumanContextStep(form: NutritionProfileCreateInput): ValidationResult {
  const errors: string[] = [];
  const humanContext = form.humanContextJson;
  
  if (!humanContext?.sleepQuality) {
    errors.push('Sleep quality is required');
  }
  if (!humanContext?.sleepPatterns) {
    errors.push('Sleep patterns is required');
  }
  if (!humanContext?.cultureIdentity) {
    errors.push('Culture identity is required');
  }
  if (!humanContext?.religiousRestrictions || humanContext.religiousRestrictions.length === 0) {
    errors.push('Religious restrictions is required');
  }
  if (!humanContext?.activityLevel) {
    errors.push('Activity level is required');
  }
  if (!humanContext?.workSchedule) {
    errors.push('Work schedule is required');
  }
  if (!humanContext?.stressLevels) {
    errors.push('Stress levels is required');
  }
  if (!humanContext?.familyStructure) {
    errors.push('Family structure is required');
  }
  if (!humanContext?.cookingResponsibility) {
    errors.push('Cooking responsibility is required');
  }
  if (!humanContext?.smokingStatus) {
    errors.push('Smoking status is required');
  }
  if (!humanContext?.alcoholConsumption) {
    errors.push('Alcohol consumption is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateConstraintsStep(form: NutritionProfileCreateInput): ValidationResult {
  // Constraints are mostly optional, but we can add validation if needed
  return { isValid: true, errors: [] };
}

function validateMealPatternStep(form: NutritionProfileCreateInput): ValidationResult {
  const errors: string[] = [];
  const mealPattern = form.mealPatternJson;
  
  if (!mealPattern) {
    errors.push('Meal pattern information is required');
    return { isValid: false, errors };
  }
  
  if (!mealPattern.typicalMealsPerDay || mealPattern.typicalMealsPerDay < 1) {
    errors.push('Typical meals per day is required and must be at least 1');
  }
  
  if (!mealPattern.mealTimes || Object.keys(mealPattern.mealTimes).length === 0) {
    errors.push('At least one meal time is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

function validateAllSteps(form: NutritionProfileCreateInput): ValidationResult {
  const allErrors: string[] = [];
  
  const basics = validateBasicsStep(form);
  if (!basics.isValid) {
    allErrors.push(...basics.errors.map(e => `Basics: ${e}`));
  }
  
  const preferences = validatePreferencesStep(form);
  if (!preferences.isValid) {
    allErrors.push(...preferences.errors.map(e => `Preferences: ${e}`));
  }
  
  const humanContext = validateHumanContextStep(form);
  if (!humanContext.isValid) {
    allErrors.push(...humanContext.errors.map(e => `Lifestyle: ${e}`));
  }
  
  const mealPattern = validateMealPatternStep(form);
  if (!mealPattern.isValid) {
    allErrors.push(...mealPattern.errors.map(e => `Meal Pattern: ${e}`));
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
}

function validateStep(stepId: StepId, form: NutritionProfileCreateInput): ValidationResult {
  switch (stepId) {
    case 'import_or_manual':
      return { isValid: true, errors: [] };
    case 'basics':
      return validateBasicsStep(form);
    case 'preferences':
      return validatePreferencesStep(form);
    case 'medical_history':
      return validateMedicalHistoryStep(form);
    case 'human_context':
      return validateHumanContextStep(form);
    case 'constraints':
      return validateConstraintsStep(form);
    case 'meal_pattern':
      return validateMealPatternStep(form);
    case 'insights':
      return { isValid: true, errors: [] };
    case 'review':
      return validateAllSteps(form);
    default:
      return { isValid: true, errors: [] };
  }
}

const PatientNutritionProfileTab: React.FC<PatientNutritionProfileTabProps> = ({ patientId }) => {
  const [activeStep, setActiveStep] = useState<StepId>('basics');
  const [profile, setProfile] = useState<NutritionProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<StepId, string[]>>({
    import_or_manual: [],
    basics: [],
    preferences: [],
    medical_history: [],
    human_context: [],
    constraints: [],
    meal_pattern: [],
    insights: [],
    review: [],
  });

  // Insights step: derived metrics/scores
  const [insightsSummary, setInsightsSummary] = useState<DerivedMetricsSummaryResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [insightsSubTab, setInsightsSubTab] = useState<'metrics' | 'scores'>('metrics');
  const [insightsEdits, setInsightsEdits] = useState<Record<string, number>>({});
  /** Set after creating a profile so we can show "insights may take 2–5 min" note; cleared when user refreshes or leaves Insights. */
  const [profileJustCreated, setProfileJustCreated] = useState(false);

  // Import from documents state
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [parseLoading, setParseLoading] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [parseResult, setParseResult] = useState<NutritionProfileParseResult | null>(null);
  /** When multiple documents are parsed, per-document extractions; null for single-doc. */
  const [parseResultsByDocument, setParseResultsByDocument] = useState<NutritionProfileExtractionItem[] | null>(null);
  /** For overlapping fields, which document's value to use (key = field key, value = document_index). Default first (0). */
  const [selectedDocumentPerField, setSelectedDocumentPerField] = useState<Record<string, number>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseMessage, setParseMessage] = useState<string | null>(null);
  const [showImportArea, setShowImportArea] = useState(false);
  const [showExtractedPreviewModal, setShowExtractedPreviewModal] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

  const [profileSectionsOpen, setProfileSectionsOpen] = useState<Record<string, boolean>>({
    basics: true,
    foodPreferences: false,
    medicalHistory: false,
    humanContext: false,
    constraints: false,
    mealPattern: false,
    notes: false,
  });
  const toggleProfileSection = (key: string) =>
    setProfileSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const [form, setForm] = useState<NutritionProfileCreateInput>({
    userId: patientId,
    capturedViaEnum: undefined,
    bodyWeightKg: undefined,
    gender: undefined,
    heightCm: undefined,
    dateOfBirth: undefined,
    waistCm: undefined,
    hipCm: undefined,
    neckCm: undefined,
    isPregnant: undefined,
    isLactating: undefined,
    lifeStage: undefined, // Will be derived from dateOfBirth
    motherTongue: undefined,
    preferencesJson: emptyPreferences(),
    humanContextJson: emptyHumanContext(),
    constraintsJson: emptyConstraints(),
    medicalHistoryJson: emptyMedicalHistory(),
    mealPatternJson: emptyMealPattern(),
    doctorNotes: '',
    patientNotes: '',
    motivationReadiness: undefined,
    previousDietAttempts: 0,
    supportSystemAvailable: undefined,
    technologyComfortLevel: undefined,
    allergiesJson: [],
  });
  
  // Track selected meal type for each slot (index -> meal type)
  const [mealTypeSelections, setMealTypeSelections] = useState<Record<number, string>>({});

  // Dictionary state
  const [dictionaries, setDictionaries] = useState<{
    capturedVia: DictionaryItem[];
    maritalStatus: DictionaryItem[];
    activityLevel: DictionaryItem[];
    motivationReadiness: DictionaryItem[];
    technologyComfortLevel: DictionaryItem[];
    dietType: DictionaryItem[];
    allergyType: DictionaryItem[];
    foodIntolerance: DictionaryItem[];
    nutritionalDeficiency: DictionaryItem[];
    waterIntake: DictionaryItem[];
    snackFrequency: DictionaryItem[];
    weekendPattern: DictionaryItem[];
    mealType: DictionaryItem[];
    sleepQuality: DictionaryItem[];
    sleepPattern: DictionaryItem[];
    workSchedule: DictionaryItem[];
    stressLevel: DictionaryItem[];
    familyStructure: DictionaryItem[];
    cookingResponsibility: DictionaryItem[];
    alcoholConsumption: DictionaryItem[];
    religiousRestriction: DictionaryItem[];
    smokingStatus: DictionaryItem[];
    diseases: DictionaryItem[];
    medication: DictionaryItem[];
    personalHistory: DictionaryItem[];
    familyHistory: DictionaryItem[];
    cultureIdentity: DictionaryItem[];
  }>({
    capturedVia: [],
    maritalStatus: [],
    activityLevel: [],
    motivationReadiness: [],
    technologyComfortLevel: [],
    dietType: [],
    allergyType: [],
    foodIntolerance: [],
    nutritionalDeficiency: [],
    waterIntake: [],
    snackFrequency: [],
    weekendPattern: [],
    mealType: [],
    sleepQuality: [],
    sleepPattern: [],
    workSchedule: [],
    stressLevel: [],
    familyStructure: [],
    cookingResponsibility: [],
    alcoholConsumption: [],
    religiousRestriction: [],
    smokingStatus: [],
    diseases: [],
    medication: [],
    personalHistory: [],
    familyHistory: [],
    cultureIdentity: [],
  });
  const [loadingDictionaries, setLoadingDictionaries] = useState(false);

  const stepIndex = useMemo(() => steps.findIndex((s) => s.id === activeStep), [activeStep]);

  /** Which steps have data loaded FROM THE DOCUMENT (after "Load into form"). Green only for these; at start all steps stay white/grey. */
  const stepsWithLoadedData = useMemo((): Set<StepId> => {
    const out = new Set<StepId>();
    const p = parseResult;
    if (!p) return out; // No parse result → no green anywhere
    out.add('import_or_manual');
    const basicsKeys = ['bodyWeightKg', 'heightCm', 'gender', 'dateOfBirth', 'waistCm', 'hipCm', 'neckCm', 'motherTongue', 'motivationReadiness', 'technologyComfortLevel', 'previousDietAttempts', 'supportSystemAvailable', 'activityLevel', 'doctorNotes', 'patientNotes'] as const;
    const basicsFromDoc = basicsKeys.some((k) => {
      const src = getSourceForFormField(p, k, (form as Record<string, unknown>)[k]);
      return src === 'from_document' || src === 'edited';
    });
    const humanContextFromDoc =
      getSourceForFormField(p, 'maritalStatus', form.humanContextJson?.maritalStatus) !== 'user_data' ||
      getSourceForFormField(p, 'activityLevel', form.humanContextJson?.activityLevel ?? form.activityLevel) !== 'user_data';
    if (basicsFromDoc) out.add('basics');
    if (humanContextFromDoc) out.add('human_context');
    const hasAnyParsedMetric =
      p.bmi != null || p.body_fat_pct != null || p.body_weight_kg != null || p.fat_mass_kg != null ||
      p.fat_free_body_weight_kg != null || p.bmr_kcal != null || p.whr != null || p.smi_kg_m2 != null ||
      p.body_water_pct != null || p.bone_mass_kg != null || p.muscle_mass_kg != null || p.subcutaneous_fat_pct != null ||
      p.visceral_fat_grade != null;
    if (hasAnyParsedMetric) out.add('insights');
    if (out.size > 0) out.add('review');
    return out;
  }, [form, parseResult]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const timeOptions = useMemo(() => buildTimeOptions(15), []);

  // Lazy-load dictionaries only for the active step when in create/edit flow (not on tab load)
  useEffect(() => {
    if (!isEditMode) return;
    const keys = STEP_DICTIONARY_KEYS[activeStep];
    if (!keys.length) return;

    let cancelled = false;
    setLoadingDictionaries(true);

    nutritionDictionaryService
      .loadDictionariesForKeys(keys)
      .then((partial) => {
        if (cancelled) return;
        setDictionaries((prev) => ({ ...prev, ...partial }));
        // Auto-populate captured_via as "doctor_visit" when basics dictionaries load
        if (partial.capturedVia) {
          setForm((prev) => {
            if (!prev.capturedViaEnum) {
              const doctorVisitOption = partial.capturedVia!.find((item) => item.code === 'doctor_visit');
              return {
                ...prev,
                capturedViaEnum: doctorVisitOption ? 'doctor_visit' : undefined,
              };
            }
            return prev;
          });
        }
      })
      .catch((error) => {
        if (!cancelled) console.warn('Failed to load step dictionaries:', error);
      })
      .finally(() => {
        if (!cancelled) setLoadingDictionaries(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeStep, isEditMode]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const existing = await nutritionProfileService.getCurrentProfileForPatient(patientId);
        if (cancelled) return;

        // Always fetch the latest unified nutrition state (vitals + derived metrics + profile snapshot)
        // and prefer those values for anthropometrics/date of birth, regardless of whether a profile exists.
        const currentState = await nutritionProfileService.getCurrentStateForPatient(patientId);
        if (cancelled) return;

        if (existing) {
          setProfile(existing);
          setIsEditMode(false); // Start in view mode when profile exists
          setForm((prev) => {
            const snapshot = currentState.profile_snapshot;
            const anthropometrics = currentState.anthropometrics;
            const demographics = currentState.demographics;

            const dateOfBirth = snapshot?.date_of_birth || demographics.date_of_birth || existing.dateOfBirth || undefined;
            const derivedLifeStage = dateOfBirth ? deriveLifeStageFromDOB(dateOfBirth) : undefined;

            return {
            ...prev,
            userId: existing.userId,
              capturedViaEnum: existing.capturedViaEnum ?? undefined,
              // Prefer current nutrition profile (snapshot / existing) for basics so Edit shows profile values, not stale vitals/user_profile
              bodyWeightKg: (snapshot?.body_weight_kg ?? existing.bodyWeightKg ?? anthropometrics.body_weight_kg) ?? undefined,
              gender: (snapshot?.gender ?? existing.gender ?? demographics.gender) ?? undefined,
              heightCm: (snapshot?.height_cm ?? existing.heightCm ?? anthropometrics.height_cm ?? demographics.height_cm) ?? undefined,
              dateOfBirth,
              waistCm: (snapshot?.waist_cm ?? existing.waistCm ?? anthropometrics.waist_cm) ?? undefined,
              hipCm: (snapshot?.hip_cm ?? existing.hipCm ?? anthropometrics.hip_cm) ?? undefined,
              neckCm: (snapshot?.neck_cm ?? existing.neckCm) ?? undefined,
              isPregnant: snapshot?.is_pregnant !== undefined
                ? snapshot.is_pregnant
                : (existing.isPregnant !== null && existing.isPregnant !== undefined ? existing.isPregnant : prev.isPregnant),
              isLactating: snapshot?.is_lactating !== undefined
                ? snapshot.is_lactating
                : (existing.isLactating !== null && existing.isLactating !== undefined ? existing.isLactating : prev.isLactating),
              lifeStage: snapshot?.life_stage || existing.lifeStage || derivedLifeStage || prev.lifeStage,
              motherTongue: snapshot?.mother_tongue || existing.motherTongue || prev.motherTongue,
              // JSON sections: prefer profile snapshot from state if present, else stored profile JSON
              preferencesJson: existing.preferencesJson || (snapshot?.preferences as any) || emptyPreferences(),
              // human context: merge activity_level from snapshot/demographics so Basics shows it
            humanContextJson: {
              ...(existing.humanContextJson || emptyHumanContext()),
              activityLevel: (snapshot as any)?.activity_level ?? existing.humanContextJson?.activityLevel ?? demographics?.activity_level ?? undefined,
            },
              constraintsJson: (snapshot?.constraints as any) || existing.constraintsJson || emptyConstraints(),
              medicalHistoryJson: (snapshot?.medical_history as any) || existing.medicalHistoryJson || emptyMedicalHistory(),
              mealPatternJson: (snapshot?.meal_pattern as any) || existing.mealPatternJson || emptyMealPattern(),
              doctorNotes: snapshot?.doctor_notes || existing.doctorNotes || '',
              patientNotes: snapshot?.patient_notes || existing.patientNotes || '',
              motivationReadiness: snapshot?.motivation_readiness || existing.motivationReadiness || prev.motivationReadiness,
              previousDietAttempts: snapshot?.previous_diet_attempts ?? existing.previousDietAttempts ?? prev.previousDietAttempts,
              supportSystemAvailable: snapshot?.support_system_available !== undefined
                ? snapshot.support_system_available
                : (existing.supportSystemAvailable ?? prev.supportSystemAvailable),
              technologyComfortLevel: snapshot?.technology_comfort_level || existing.technologyComfortLevel || prev.technologyComfortLevel,
            allergiesJson: existing.allergiesJson || [],
            };
          });
          
          // Initialize meal type selections from loaded mealTimes
          const loadedMealPattern = existing.mealPatternJson || emptyMealPattern();
          if (loadedMealPattern?.mealTimes) {
            const selections: Record<number, string> = {};
            Object.entries(loadedMealPattern.mealTimes).forEach(([mealType, time], idx) => {
              const slotIndex = DEFAULT_MEAL_TYPE_ORDER.indexOf(mealType as typeof DEFAULT_MEAL_TYPE_ORDER[number]);
              if (slotIndex >= 0) {
                selections[slotIndex] = mealType;
              } else {
                // If not in defaults, assign to first available slot
                for (let i = 0; i < 6; i++) {
                  if (!selections[i]) {
                    selections[i] = mealType;
                    break;
                  }
                }
              }
            });
            setMealTypeSelections(selections);
          }
        } else {
          setProfile(null);
          setIsEditMode(false); // Will be set to true when user clicks "Create Profile" or when restoring draft
          const draft = loadNutritionProfileDraft(patientId);
          if (draft) {
            setForm({ ...draft.form, userId: patientId });
            setActiveStep(draft.activeStep);
            setParseResult(draft.parseResult);
            setParseResultsByDocument(draft.parseResultsByDocument);
            setSelectedDocumentPerField(draft.selectedDocumentPerField || {});
            setInsightsEdits(draft.insightsEdits || {});
            setIsEditMode(true);
            if (draft.parseResult != null) setShowImportArea(true);
          } else {
          // Load current nutrition state to pre-fill form with latest vitals/onboarding data
          try {
            const currentState = await nutritionProfileService.getCurrentStateForPatient(patientId);
            if (currentState) {
              // Pre-fill form with current state (latest vitals + any existing profile snapshot)
              setForm((prev) => {
                const snapshot = currentState.profile_snapshot;
                const anthropometrics = currentState.anthropometrics;
                const demographics = currentState.demographics;
                
                return {
                  ...prev,
                  userId: patientId,
                  gender: snapshot?.gender || demographics.gender || prev.gender,
                  heightCm: anthropometrics.height_cm || snapshot?.height_cm || demographics.height_cm || prev.heightCm,
                  bodyWeightKg: anthropometrics.body_weight_kg || snapshot?.body_weight_kg || prev.bodyWeightKg,
                  waistCm: anthropometrics.waist_cm || snapshot?.waist_cm || prev.waistCm,
                  hipCm: anthropometrics.hip_cm || snapshot?.hip_cm || prev.hipCm,
                  neckCm: snapshot?.neck_cm || prev.neckCm,
                  dateOfBirth: snapshot?.date_of_birth || demographics.date_of_birth || prev.dateOfBirth,
                  lifeStage: snapshot?.life_stage || (demographics.date_of_birth ? deriveLifeStageFromDOB(demographics.date_of_birth) : prev.lifeStage),
                  isPregnant: snapshot?.is_pregnant !== undefined ? snapshot.is_pregnant : prev.isPregnant,
                  isLactating: snapshot?.is_lactating !== undefined ? snapshot.is_lactating : prev.isLactating,
                  motherTongue: prev.motherTongue, // Not in snapshot, keep existing
                  preferencesJson: prev.preferencesJson || snapshot?.preferences || emptyPreferences(),
                  humanContextJson: {
                    ...(prev.humanContextJson || emptyHumanContext()),
                    // Prefill activity level from demographics if not already set
                    activityLevel: prev.humanContextJson?.activityLevel || 
                                   demographics.activity_level || 
                                   undefined,
                  },
                  constraintsJson: prev.constraintsJson || snapshot?.constraints || emptyConstraints(),
                  medicalHistoryJson: prev.medicalHistoryJson || snapshot?.medical_history || emptyMedicalHistory(),
                  mealPatternJson: prev.mealPatternJson || snapshot?.meal_pattern || emptyMealPattern(),
                  motivationReadiness: snapshot?.motivation_readiness || prev.motivationReadiness,
                  previousDietAttempts: snapshot?.previous_diet_attempts ?? prev.previousDietAttempts,
                  supportSystemAvailable: snapshot?.support_system_available !== undefined ? snapshot.support_system_available : prev.supportSystemAvailable,
                  technologyComfortLevel: snapshot?.technology_comfort_level || prev.technologyComfortLevel,
                  doctorNotes: snapshot?.doctor_notes || prev.doctorNotes,
                  patientNotes: snapshot?.patient_notes || prev.patientNotes,
                };
              });
            }
          } catch (stateError: any) {
            // Non-fatal: if we can't load current state, just proceed with empty form
            console.warn('Failed to load current nutrition state for pre-fill:', stateError);
          }
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load nutrition profile', e);
        setError(e?.response?.data?.detail || e?.message || 'Failed to load nutrition profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const fetchInsights = useCallback(() => {
    if (!patientId) return;
    setInsightsLoading(true);
    setInsightsError(null);
    setProfileJustCreated(false); // Clear "just created" note when user refreshes
    derivedMetricsService.getSummary(patientId).then((summary) => {
      setInsightsSummary(summary);
      setInsightsLoading(false);
    }).catch((e: unknown) => {
      const err = e as { response?: { data?: { detail?: string }; status?: number }; message?: string };
      setInsightsError(err?.response?.data?.detail || err?.message || 'Failed to load insights');
      setInsightsLoading(false);
    });
  }, [patientId]);

  // Load derived metrics when Insights step is active
  useEffect(() => {
    if (activeStep !== 'insights' || !patientId) return;
    fetchInsights();
  }, [activeStep, patientId, fetchInsights]);

  // Clear "profile just created" note when leaving Insights step
  useEffect(() => {
    if (activeStep !== 'insights') setProfileJustCreated(false);
  }, [activeStep]);

  // Persist draft to localStorage when creating (no profile yet); survives refresh/network loss
  const draftPersistRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!patientId || !isEditMode || profile != null) return;
    const persist = () => {
      saveNutritionProfileDraft(patientId, {
        activeStep,
        form,
        parseResult,
        parseResultsByDocument,
        selectedDocumentPerField,
        insightsEdits,
      });
    };
    if (parseResult != null || (parseResultsByDocument != null && parseResultsByDocument.length > 0)) {
      persist();
    }
    if (draftPersistRef.current) clearTimeout(draftPersistRef.current);
    draftPersistRef.current = setTimeout(persist, 500);
    return () => {
      if (draftPersistRef.current) clearTimeout(draftPersistRef.current);
      draftPersistRef.current = null;
    };
  }, [patientId, isEditMode, profile, activeStep, form, parseResult, parseResultsByDocument, selectedDocumentPerField, insightsEdits]);

  // Extraction progress is driven by parseDocuments onProgress (linked to actual poll status)

  // Ensure default values are always saved for all tabs
  // This runs after loading completes to merge defaults with existing values
  useEffect(() => {
    if (loading) return;
    
    setForm((prev) => {
      const updates: Partial<NutritionProfileCreateInput> = {};
      let hasChanges = false;

      // Lifestyle - ensure activityLevel default is set
      const humanContext = prev.humanContextJson || emptyHumanContext();
      if (!prev.humanContextJson || prev.humanContextJson.activityLevel === undefined || prev.humanContextJson.activityLevel === null) {
        updates.humanContextJson = {
          ...humanContext,
          activityLevel: 'moderate',
        };
        hasChanges = true;
      }

      // Constraints - ensure all default values are set
      const constraints = prev.constraintsJson || emptyConstraints();
      const defaultConstraints = emptyConstraints();
      const defaultCooking = defaultConstraints.cooking || { has_stove: true, has_microwave: true, has_refrigerator: true, has_oven: false };
      const defaultBudget = defaultConstraints.budget || { monthly_budget: undefined, currency: 'INR' };
      const defaultSchedule = defaultConstraints.schedule || { work_hours: '', meal_prep_time_min: 30 };
      
      const needsConstraintsUpdate = 
        !prev.constraintsJson ||
        !constraints.cooking ||
        constraints.cooking.has_stove === undefined ||
        constraints.cooking.has_microwave === undefined ||
        constraints.cooking.has_refrigerator === undefined ||
        constraints.cooking.has_oven === undefined ||
        !constraints.budget ||
        constraints.budget.currency === undefined ||
        !constraints.schedule ||
        constraints.schedule.meal_prep_time_min === undefined;
      
      if (needsConstraintsUpdate) {
        updates.constraintsJson = {
          cooking: {
            has_stove: constraints.cooking?.has_stove ?? defaultCooking.has_stove,
            has_microwave: constraints.cooking?.has_microwave ?? defaultCooking.has_microwave,
            has_refrigerator: constraints.cooking?.has_refrigerator ?? defaultCooking.has_refrigerator,
            has_oven: constraints.cooking?.has_oven ?? defaultCooking.has_oven,
          },
          budget: {
            monthly_budget: constraints.budget?.monthly_budget,
            currency: constraints.budget?.currency || defaultBudget.currency,
          },
          schedule: {
            work_hours: constraints.schedule?.work_hours || '',
            meal_prep_time_min: constraints.schedule?.meal_prep_time_min ?? defaultSchedule.meal_prep_time_min,
          },
          wontDo: constraints.wontDo || [],
        };
        hasChanges = true;
      }

      // Meal Pattern - ensure all default values are set
      const mealPattern = prev.mealPatternJson || emptyMealPattern();
      const defaultMealPattern = emptyMealPattern();
      const needsMealPatternUpdate =
        !prev.mealPatternJson ||
        mealPattern.typicalMealsPerDay === undefined ||
        mealPattern.typicalMealsPerDay === null ||
        mealPattern.snackFrequency === undefined ||
        mealPattern.snackFrequency === null ||
        mealPattern.lateNightEating === undefined ||
        mealPattern.lateNightEating === null ||
        mealPattern.weekendPatterns === undefined ||
        mealPattern.weekendPatterns === null;
      
      if (needsMealPatternUpdate) {
        updates.mealPatternJson = {
          typicalMealsPerDay: mealPattern.typicalMealsPerDay ?? defaultMealPattern.typicalMealsPerDay,
          snackFrequency: mealPattern.snackFrequency || defaultMealPattern.snackFrequency,
          lateNightEating: mealPattern.lateNightEating ?? defaultMealPattern.lateNightEating,
          weekendPatterns: mealPattern.weekendPatterns || defaultMealPattern.weekendPatterns,
          mealTimes: mealPattern.mealTimes || {},
        };
        hasChanges = true;
      }

      // Initialize default meal times if needed
      const finalMealPattern = updates.mealPatternJson || mealPattern;
      const mealsPerDay = finalMealPattern.typicalMealsPerDay;
      const mealTimes = finalMealPattern.mealTimes || {};
      
      if (mealsPerDay && mealsPerDay > 0 && Object.keys(mealTimes).length === 0) {
        const newMealTimes: Record<string, string> = {};
        const newSelections: Record<number, string> = {};
        
        for (let i = 0; i < mealsPerDay; i++) {
          const defaultMealType = DEFAULT_MEAL_TYPE_ORDER[i] ?? 'breakfast';
          const defaultTime = DEFAULT_MEAL_TIMES[i] ?? '12:00';
          newMealTimes[defaultMealType] = defaultTime;
          newSelections[i] = defaultMealType;
        }
        
        if (updates.mealPatternJson) {
          updates.mealPatternJson.mealTimes = newMealTimes;
        } else {
          updates.mealPatternJson = {
            ...mealPattern,
            mealTimes: newMealTimes,
          };
        }
        
        setMealTypeSelections(newSelections);
        hasChanges = true;
      }

      if (hasChanges) {
        return {
          ...prev,
          ...updates,
        };
      }
      return prev;
    });
  }, [loading]); // Run when loading completes

  const goNext = () => {
    // Validate current step before moving forward
    const validation = validateStep(activeStep, form);
    
    if (!validation.isValid) {
      setValidationErrors((prev) => ({
        ...prev,
        [activeStep]: validation.errors,
      }));
      setError(`Please fix the following errors before proceeding:\n• ${validation.errors.join('\n• ')}`);
      return;
    }
    
    // Clear validation errors for current step
    setValidationErrors((prev) => ({
      ...prev,
      [activeStep]: [],
    }));
    // Clear general error if it was a validation error
    if (error && error.includes('Please fix the following errors')) {
      setError(null);
    }
    
    const next = steps[Math.min(stepIndex + 1, steps.length - 1)];
    setActiveStep(next.id);
  };

  const goBack = () => {
    const prev = steps[Math.max(stepIndex - 1, 0)];
    setActiveStep(prev.id);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setActiveStep('import_or_manual');
    setShowExtractedPreviewModal(false);
    setError(null);
    setValidationErrors({
      import_or_manual: [],
      basics: [],
      preferences: [],
      medical_history: [],
      human_context: [],
      constraints: [],
      meal_pattern: [],
      insights: [],
      review: [],
    });
    setShowImportArea(false);
    setImportFiles([]);
    setParseResult(null);
    setParseError(null);
    setParseMessage(null);
    // Reset form to original profile data if it exists
    if (profile) {
      const dateOfBirth = profile.dateOfBirth ?? undefined;
      const derivedLifeStage = dateOfBirth ? deriveLifeStageFromDOB(dateOfBirth) : undefined;
      setForm({
        userId: profile.userId,
        capturedViaEnum: profile.capturedViaEnum ?? undefined,
        bodyWeightKg: profile.bodyWeightKg ?? undefined,
        gender: profile.gender ?? undefined,
        heightCm: profile.heightCm ?? undefined,
        dateOfBirth: dateOfBirth,
        waistCm: profile.waistCm ?? undefined,
        hipCm: profile.hipCm ?? undefined,
        neckCm: profile.neckCm ?? undefined,
        lifeStage: profile.lifeStage ?? derivedLifeStage ?? undefined,
        motherTongue: profile.motherTongue ?? undefined,
        preferencesJson: profile.preferencesJson || emptyPreferences(),
        humanContextJson: profile.humanContextJson || emptyHumanContext(),
        constraintsJson: profile.constraintsJson || emptyConstraints(),
        medicalHistoryJson: profile.medicalHistoryJson || emptyMedicalHistory(),
        mealPatternJson: profile.mealPatternJson || emptyMealPattern(),
        doctorNotes: profile.doctorNotes || '',
        patientNotes: profile.patientNotes || '',
        motivationReadiness: profile.motivationReadiness ?? undefined,
        previousDietAttempts: profile.previousDietAttempts ?? 0,
        supportSystemAvailable: profile.supportSystemAvailable ?? undefined,
        technologyComfortLevel: profile.technologyComfortLevel ?? undefined,
        allergiesJson: profile.allergiesJson || [],
      });
    }
  };

  const isCompositeScoreItem = (item: { category?: string | null }) =>
    item.category === 'Composite Score' || (item.category?.toLowerCase() ?? '').includes('composite');

  /** Composite score metric_key names accepted by backend derived-scores API (health_metric_scores.name). Ensures body_score etc. from document/edits are saved even if Insights tab wasn't loaded. Must match backend metric_scores.json names. */
  const COMPOSITE_SCORE_SAVE_KEYS = [
    'body_score',
    'wellness',
    'cmri',
    'bci',
    'srbi',
    'metabolic_fitness_score',
    'inflammatory_burden_score',
    'biological_age_delta',
  ];

  /** Save only modified or document-sourced insight values (not backend-only). Build vitals and score entries from parseResult + insightsEdits only. */
  const saveDisplayedInsightsToVitals = useCallback(async () => {
    const items = insightsSummary?.items ?? [];
    const itemByKey = new Map(items.map((i) => [i.metric_key, i]));
    const recordedAt = new Date().toISOString();
    const entries: Array<{ metric_type: string; value: number; unit: string; recorded_at: string; notes: string; source_device: string; data_source?: string }> = [];
    const vitalKeys = new Set<string>([
      ...Object.keys(insightsEdits).filter((k) => VITAL_BACKED_DERIVED_METRICS[k]),
      ...(parseResult ? Object.keys(parseResult).filter((k) => VITAL_BACKED_DERIVED_METRICS[k]) : []),
    ]);
    Array.from(vitalKeys).forEach((metricKey) => {
      const vitalType = VITAL_BACKED_DERIVED_METRICS[metricKey];
      if (!vitalType) return;
      const fromEdit = insightsEdits[metricKey] !== undefined && Number.isFinite(Number(insightsEdits[metricKey]));
      const value = fromEdit ? Number(insightsEdits[metricKey]) : getParsedValueForMetric(parseResult, metricKey);
      if (value == null || !Number.isFinite(value)) return;
      const item = itemByKey.get(metricKey);
      const fromDocument = !fromEdit && parseResult && getParsedValueForMetric(parseResult, metricKey) != null;
      entries.push({
        metric_type: vitalType,
        value: Number(value),
        unit: item?.unit || item?.reference_unit || '',
        recorded_at: recordedAt,
        notes: `Nutrition profile save (${item?.display_name ?? metricKey})`,
        source_device: 'Doctor Portal Insights',
        ...(fromDocument ? { data_source: 'document_extraction' } : {}),
      });
    });
    const scoreEntries: Array<{ score_name: string; value: number; recorded_at: string }> = [];
    const addedScoreKeys = new Set<string>();
    items.forEach((item) => {
      if (!isCompositeScoreItem(item)) return;
      const value =
        insightsEdits[item.metric_key] !== undefined && Number.isFinite(Number(insightsEdits[item.metric_key]))
          ? Number(insightsEdits[item.metric_key])
          : getParsedValueForMetric(parseResult, item.metric_key);
      if (value == null || !Number.isFinite(value)) return;
      addedScoreKeys.add((item.metric_key || '').toLowerCase());
      scoreEntries.push({
        score_name: item.metric_key,
        value: Number(value),
        recorded_at: recordedAt,
      });
    });
    // Also add composite scores from document/edits when Insights summary wasn't loaded (e.g. body_score from document)
    COMPOSITE_SCORE_SAVE_KEYS.forEach((key) => {
      if (addedScoreKeys.has(key.toLowerCase())) return;
      const value =
        insightsEdits[key] !== undefined && Number.isFinite(Number(insightsEdits[key]))
          ? Number(insightsEdits[key])
          : getParsedValueForMetric(parseResult, key);
      if (value == null || !Number.isFinite(value)) return;
      scoreEntries.push({ score_name: key, value: Number(value), recorded_at: recordedAt });
    });
    try {
      if (entries.length > 0) {
        await derivedMetricsService.saveInsightsVitals(patientId, { entries });
      }
      if (scoreEntries.length > 0) {
        await derivedMetricsService.saveInsightsDerivedScores(patientId, { entries: scoreEntries });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to persist insights to vitals/scores (profile was saved):', e);
    }
  }, [patientId, parseResult, insightsEdits, insightsSummary?.items]);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate all required fields before saving
      const validation = validateAllSteps(form);
      if (!validation.isValid) {
        setError(`Please fix the following errors before saving:\n• ${validation.errors.join('\n• ')}`);
        setSaving(false);
        return;
      }

      if (profile) {
        const update: NutritionProfileUpdateInput = {
          bodyWeightKg: form.bodyWeightKg,
          activityLevel: form.activityLevel,
          gender: form.gender,
          heightCm: form.heightCm,
          dateOfBirth: form.dateOfBirth,
          waistCm: form.waistCm,
          hipCm: form.hipCm,
          neckCm: form.neckCm,
          isPregnant: form.isPregnant,
          isLactating: form.isLactating,
          lifeStage: form.lifeStage,
          motherTongue: form.motherTongue,
          preferencesJson: form.preferencesJson,
          humanContextJson: form.humanContextJson,
          constraintsJson: form.constraintsJson,
          medicalHistoryJson: form.medicalHistoryJson,
          mealPatternJson: form.mealPatternJson,
          motivationReadiness: form.motivationReadiness,
          previousDietAttempts: form.previousDietAttempts,
          supportSystemAvailable: form.supportSystemAvailable,
          technologyComfortLevel: form.technologyComfortLevel,
          allergiesJson: form.allergiesJson,
          doctorNotes: form.doctorNotes,
          patientNotes: form.patientNotes,
          capturedViaEnum: form.capturedViaEnum,
        };
        const updated = await nutritionProfileService.updateProfile(profile.id, update);
        setProfile(updated);
        // Persist displayed insights (including from-document values like body_score) before clearing draft
        await saveDisplayedInsightsToVitals();
        clearNutritionProfileDraft(patientId);
        // Profile saved successfully; refetch insights so Insights tab shows backend-derived values (vitals sync)
        derivedMetricsService.getSummary(patientId).then((summary) => setInsightsSummary(summary)).catch(() => {});
        setIsEditMode(false); // Return to view mode after saving
        setActiveStep('basics'); // Reset to first step
      } else {
        const created = await nutritionProfileService.createProfile({
          ...form,
          userId: patientId,
          capturedViaEnum: form.capturedViaEnum || 'doctor_visit', // Default to doctor_visit if not provided
        });
        setProfile(created);
        setProfileJustCreated(true); // Show "insights may take 2–5 min" note when they open Insights
        // Persist displayed insights (including from-document values like body_score) before clearing draft
        await saveDisplayedInsightsToVitals();
        clearNutritionProfileDraft(patientId);
        // Profile created successfully; refetch insights so Insights tab shows backend-derived values (vitals sync)
        derivedMetricsService.getSummary(patientId).then((summary) => setInsightsSummary(summary)).catch(() => {});
        setIsEditMode(false); // Return to view mode after creating
        setActiveStep('basics'); // Reset to first step
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to save nutrition profile', e);
      setError(e?.response?.data?.detail || e?.message || 'Failed to save nutrition profile');
    } finally {
      setSaving(false);
    }
  };

  // Note: Doctors are not allowed to delete nutrition profiles from the portal.
  // Deletion is only available to patients via their own apps.

  if (loading) {
    return <div className="section-card">Loading nutrition profile…</div>;
  }

  const preferences = form.preferencesJson || emptyPreferences();
  const humanContext = form.humanContextJson || emptyHumanContext();
  const constraints = form.constraintsJson || emptyConstraints();
  const mealPattern = form.mealPatternJson || emptyMealPattern();

  const applyParsedToForm = (extracted: NutritionProfileParseResult) => {
    const extractedDob = extracted.date_of_birth ?? undefined;
    const derivedLifeStage = extractedDob ? deriveLifeStageFromDOB(extractedDob) : (extracted.life_stage ?? undefined);
    const religiousRestrictions = extracted.religious_restrictions
      ? (typeof extracted.religious_restrictions === 'string'
          ? extracted.religious_restrictions.split(',').map((s) => s.trim()).filter(Boolean)
          : [])
      : undefined;
    const wontDo = extracted.wont_do
      ? (typeof extracted.wont_do === 'string'
          ? extracted.wont_do.split(',').map((s) => s.trim()).filter(Boolean)
          : [])
      : undefined;
    setForm((prev) => {
      const prevHuman = prev.humanContextJson || emptyHumanContext();
      const prevConstraints = prev.constraintsJson || emptyConstraints();
      return ({
      ...prev,
      gender: extracted.gender ?? prev.gender,
      dateOfBirth: extractedDob ?? prev.dateOfBirth,
      heightCm: extracted.height_cm ?? prev.heightCm,
      bodyWeightKg: extracted.body_weight_kg ?? prev.bodyWeightKg,
      waistCm: extracted.waist_cm ?? prev.waistCm,
      hipCm: extracted.hip_cm ?? prev.hipCm,
      neckCm: extracted.neck_cm ?? prev.neckCm,
      isPregnant: extracted.is_pregnant ?? prev.isPregnant,
      isLactating: extracted.is_lactating ?? prev.isLactating,
      lifeStage: derivedLifeStage ?? extracted.life_stage ?? prev.lifeStage,
      motherTongue: extracted.mother_tongue ?? prev.motherTongue,
      activityLevel: extracted.activity_level ?? prev.activityLevel,
      motivationReadiness: extracted.motivation_readiness ?? prev.motivationReadiness,
      technologyComfortLevel: extracted.technology_comfort_level ?? prev.technologyComfortLevel,
      previousDietAttempts: extracted.previous_diet_attempts ?? prev.previousDietAttempts,
      supportSystemAvailable: extracted.support_system_available ?? prev.supportSystemAvailable,
      doctorNotes: extracted.doctor_notes ?? prev.doctorNotes,
      patientNotes: extracted.patient_notes ?? prev.patientNotes,
      preferencesJson: {
        ...(prev.preferencesJson || emptyPreferences()),
        typesOfDiet: extracted.types_of_diet ?? prev.preferencesJson?.typesOfDiet ?? null,
        typesOfDietNotes: extracted.diet_notes ?? prev.preferencesJson?.typesOfDietNotes ?? null,
        favoriteFood: extracted.favorite_food ?? prev.preferencesJson?.favoriteFood ?? null,
        dislikedFood: extracted.disliked_food ?? prev.preferencesJson?.dislikedFood ?? null,
        allergies: extracted.allergies ?? prev.preferencesJson?.allergies ?? null,
        allergiesNotes: extracted.allergies_notes ?? prev.preferencesJson?.allergiesNotes ?? null,
        foodIntolerances: extracted.food_intolerances ?? prev.preferencesJson?.foodIntolerances ?? null,
        foodIntolerancesNotes: extracted.food_intolerances_notes ?? prev.preferencesJson?.foodIntolerancesNotes ?? null,
        nutritionalDeficiencies: extracted.nutritional_deficiencies ?? prev.preferencesJson?.nutritionalDeficiencies ?? null,
        nutritionalDeficienciesNotes: extracted.nutritional_deficiencies_notes ?? prev.preferencesJson?.nutritionalDeficienciesNotes ?? null,
        waterIntake: extracted.water_intake ?? prev.preferencesJson?.waterIntake ?? null,
        otherInformation: extracted.preferences_other ?? prev.preferencesJson?.otherInformation ?? null,
      },
      humanContextJson: {
        ...(prev.humanContextJson || emptyHumanContext()),
        maritalStatus: extracted.marital_status ?? prev.humanContextJson?.maritalStatus,
        activityLevel: extracted.activity_level ?? prev.humanContextJson?.activityLevel,
        cultureIdentity: extracted.culture_identity ?? prev.humanContextJson?.cultureIdentity ?? '',
        religiousRestrictions: religiousRestrictions ?? prevHuman.religiousRestrictions,
        sleepPatterns: extracted.sleep_patterns ?? prev.humanContextJson?.sleepPatterns ?? '',
        stressLevels: extracted.stress_levels ?? prev.humanContextJson?.stressLevels ?? '',
        workSchedule: extracted.work_schedule ?? prev.humanContextJson?.workSchedule ?? '',
        familyStructure: extracted.family_structure ?? prev.humanContextJson?.familyStructure ?? '',
        cookingResponsibility: extracted.cooking_responsibility ?? prev.humanContextJson?.cookingResponsibility ?? '',
        sleepQuality: extracted.sleep_quality ?? prev.humanContextJson?.sleepQuality ?? null,
        smokingStatus: extracted.smoking_status ?? prev.humanContextJson?.smokingStatus ?? null,
        alcoholConsumption: extracted.alcohol_consumption ?? prev.humanContextJson?.alcoholConsumption ?? null,
      },
      medicalHistoryJson: {
        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
        diseases: extracted.diseases ?? prev.medicalHistoryJson?.diseases ?? null,
        diseasesNotes: extracted.diseases_notes ?? prev.medicalHistoryJson?.diseasesNotes ?? null,
        medication: extracted.medication ?? prev.medicalHistoryJson?.medication ?? null,
        medicationNotes: extracted.medication_notes ?? prev.medicalHistoryJson?.medicationNotes ?? null,
        personalHistory: extracted.personal_history ?? prev.medicalHistoryJson?.personalHistory ?? null,
        personalHistoryNotes: extracted.personal_history_notes ?? prev.medicalHistoryJson?.personalHistoryNotes ?? null,
        familyHistory: extracted.family_history ?? prev.medicalHistoryJson?.familyHistory ?? null,
        familyHistoryNotes: extracted.family_history_notes ?? prev.medicalHistoryJson?.familyHistoryNotes ?? null,
        otherInformation: extracted.medical_history_other ?? prev.medicalHistoryJson?.otherInformation ?? null,
      },
      constraintsJson: {
        ...(prev.constraintsJson || emptyConstraints()),
        cooking: {
          ...(prev.constraintsJson?.cooking || emptyConstraints().cooking),
          cooking_skills_level: (extracted.cooking_skills_level as 'beginner' | 'intermediate' | 'advanced') ?? (prev.constraintsJson?.cooking as any)?.cooking_skills_level,
        },
        budget: {
          monthly_budget: extracted.monthly_budget ?? (prev.constraintsJson?.budget?.monthly_budget),
          currency: extracted.budget_currency ?? prev.constraintsJson?.budget?.currency ?? 'INR',
        },
        schedule: {
          work_hours: extracted.work_hours ?? prev.constraintsJson?.schedule?.work_hours ?? '',
          meal_prep_time_min: extracted.meal_prep_time_min ?? prev.constraintsJson?.schedule?.meal_prep_time_min ?? 30,
          eatingOutFrequency: (extracted.eating_out_frequency as 'rarely' | 'sometimes' | 'often') ?? prev.constraintsJson?.schedule?.eatingOutFrequency,
        },
        wontDo: wontDo ?? prevConstraints.wontDo,
      },
      mealPatternJson: {
        ...(prev.mealPatternJson || emptyMealPattern()),
        typicalMealsPerDay: extracted.typical_meals_per_day ?? prev.mealPatternJson?.typicalMealsPerDay ?? 3,
        snackFrequency: extracted.snack_frequency ?? prev.mealPatternJson?.snackFrequency ?? 'sometimes',
        lateNightEating: extracted.late_night_eating ?? prev.mealPatternJson?.lateNightEating ?? false,
        weekendPatterns: extracted.weekend_patterns ?? prev.mealPatternJson?.weekendPatterns ?? 'same_as_weekday',
      },
    });
    });
    setActiveStep('basics');
  };

  const handleParseDocuments = async () => {
    if (importFiles.length === 0) {
      setParseError('Please select at least one file (image or PDF).');
      return;
    }
    const oversized = importFiles.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      setParseError(`File(s) exceed 10MB: ${oversized.map((f) => f.name).join(', ')}. Max 10MB per file.`);
      return;
    }
    setParseError(null);
    setParseMessage(null);
    setParseResult(null);
    setParseResultsByDocument(null);
    setSelectedDocumentPerField({});
    setParseLoading(true);
    setExtractionProgress(5); // Upload / job started
    try {
      const { extracted, message, extractions } = await nutritionProfileService.parseDocuments(
        patientId,
        importFiles,
        (attempt, status) => {
          if (status === 'complete' || status === 'failed') {
            setExtractionProgress(100);
          } else {
            // Pending: advance progress with each poll, cap at 90% until complete
            setExtractionProgress((p) => Math.min(90, 10 + attempt * 4));
          }
        },
      );
      setExtractionProgress(100);
      setParseMessage(message);
      if (extractions && extractions.length > 1) {
        setParseResultsByDocument(extractions);
        setSelectedDocumentPerField({}); // default: first document for all overlapping fields
        setParseResult(mergeExtractions(extractions, {}));
      } else {
        setParseResultsByDocument(null);
        setSelectedDocumentPerField({});
        setParseResult(extracted);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const isTimeout = e?.code === 'ECONNABORTED' || e?.message?.includes('timeout');
      const is503504 = status === 503 || status === 504;
      let message = e?.response?.data?.detail || e?.message || 'Failed to parse documents.';
      if (is503504 || isTimeout) {
        message =
          'Document parsing is taking longer than expected (the server may still be processing). Please try again in a moment, or try with fewer or smaller files.';
      }
      setParseError(message);
    } finally {
      setParseLoading(false);
      setExtractionProgress(0);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
    if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const renderStep = () => {
    switch (activeStep) {
      case 'import_or_manual':
        return (
          <div className="np-step">
            <div className="np-step-one-wrap">
              {!showImportArea ? (
                <>
                  <h3 className="np-step-one-title">How would you like to build this profile?</h3>
                  <p className="np-step-one-subtitle">
                    Upload existing medical documents for AI-assisted extraction or enter the details manually.
                  </p>
                  <div className="np-step-one-cards">
                    <button
                      type="button"
                      className="np-step-one-card np-step-one-card-primary"
                      onClick={() => setShowImportArea(true)}
                    >
                      <div className="np-step-one-card-icon np-step-one-card-icon-ai">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" fill="currentColor" />
                          <path d="M6 15l1.5 4.5L12 21l4.5-1.5L18 15l-4.5-1.5L12 9l-4.5 4.5L6 15z" fill="currentColor" opacity="0.8" />
                        </svg>
                      </div>
                      <h4 className="np-step-one-card-title">Auto-Extract from Documents</h4>
                      <p className="np-step-one-card-desc">
                        Upload clinic notes or reports. Our AI will automatically populate the nutrition profile for your review.
                      </p>
                      <span className="np-step-one-card-cta">
                        Get Started <span aria-hidden>→</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="np-step-one-card np-step-one-card-secondary"
                      onClick={() => setActiveStep('basics')}
                    >
                      <div className="np-step-one-card-icon np-step-one-card-icon-manual">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor" />
                          <path d="M5 4h3v2H5V4zm0 4h3v2H5V8zm0 4h2v2H5v-2z" fill="currentColor" opacity="0.6" />
                        </svg>
                      </div>
                      <h4 className="np-step-one-card-title">Manual Entry</h4>
                      <p className="np-step-one-card-desc">
                        Fill in the patient&apos;s data step-by-step from scratch using our standardized medical form.
                      </p>
                      <span className="np-step-one-card-cta np-step-one-card-cta-muted">
                        Start Empty <span aria-hidden>+</span>
                      </span>
                    </button>
                  </div>
                </>
              ) : parseResult && !parseError ? (
                /* After extraction: only show review block (no upload card, no step title needed) */
                <div className="np-step-one-wrap" style={{ maxWidth: '520px', margin: '0 auto' }}>
                  <p style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text)', marginTop: 0, marginBottom: '12px' }}>
                    Extraction complete. Review and load into form if correct.
                  </p>
                  <div style={{ marginTop: '12px', padding: '14px 16px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                      Preview the extracted values, then load them into the form if correct.
                    </p>
                    <button
                      type="button"
                      className="primary-button"
                      onClick={() => setShowExtractedPreviewModal(true)}
                    >
                      Preview extracted details
                    </button>
                  </div>
                  <p style={{ marginTop: '20px', marginBottom: 0 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setParseResult(null);
                        setParseMessage(null);
                        setImportFiles([]);
                      }}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: '0.9rem', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      ← Back to upload again
                    </button>
                  </p>
                </div>
              ) : parseLoading ? (
                /* Processing step: AI extracting with selected documents and progress bar */
                <div className="np-step-one-wrap" style={{ maxWidth: '560px', margin: '0 auto' }}>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden style={{ color: 'var(--primary, #7c3aed)' }}>
                        <path d="M12 2a4 4 0 0 0-4 4v1H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4zm0 2a2 2 0 0 1 2 2v1h-4V6a2 2 0 0 1 2-2zm-4 6h8v2H8v-2zm0 4h8v2H8v-2z" fill="currentColor" />
                      </svg>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>AI is extracting data...</h3>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                      This typically takes 1–2 minutes. We will notify you once ready for review.
                    </p>
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Selected documents</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {importFiles.map((file, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 14px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <div style={{ color: 'var(--primary, #7c3aed)', flexShrink: 0 }}>
                            <FileText size={24} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: '#111827', fontSize: '0.9rem' }}>{file.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatFileSize(file.size)} • Ready for parsing</div>
                          </div>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827' }}>Processing extraction</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary, #7c3aed)' }}>{Math.round(extractionProgress)}%</span>
                    </div>
                    <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${extractionProgress}%`,
                          background: 'var(--primary, #7c3aed)',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Upload Medical Documents card: drag-drop, Browse Files, Back, Cancel */}
                  <div className="np-step-one-wrap" style={{ maxWidth: '520px', margin: '0 auto' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Upload Medical Documents</h3>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '20px' }}>
                      Drag and drop files here or browse to upload clinic notes, blood reports, or previous meal plans.
                    </p>
                    <div
                      role="button"
                      tabIndex={0}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.setAttribute('data-drag', 'true'); }}
                      onDragLeave={(e) => { e.currentTarget.removeAttribute('data-drag'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.removeAttribute('data-drag');
                        const list = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
                        const valid = list.filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf');
                        if (valid.length) setImportFiles(valid);
                        setParseError(null);
                        setParseResult(null);
                      }}
                      onClick={() => importFileInputRef.current?.click()}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); importFileInputRef.current?.click(); } }}
                      style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'var(--surface, #fafafa)',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                      className="np-upload-zone"
                    >
                      <input
                        ref={importFileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const list = e.target.files ? Array.from(e.target.files) : [];
                          setImportFiles(list);
                          setParseError(null);
                          setParseResult(null);
                          e.target.value = '';
                        }}
                      />
                      <div style={{ marginBottom: '16px', color: 'var(--primary, #7c3aed)' }}>
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden style={{ margin: '0 auto' }}>
                          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" fill="currentColor" />
                        </svg>
                      </div>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={(e) => { e.stopPropagation(); importFileInputRef.current?.click(); }}
                        style={{ marginBottom: '12px' }}
                      >
                        Browse Files
                      </button>
                      <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>Supported formats: PDF, JPG, PNG (Max 10MB)</p>
                    </div>
                    {importFiles.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Selected documents</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {importFiles.map((file, i) => (
                            <div
                              key={`${file.name}-${i}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 14px',
                                background: '#f0fdf4',
                                borderRadius: '8px',
                                border: '1px solid #bbf7d0',
                              }}
                            >
                              <div style={{ color: 'var(--primary, #7c3aed)', flexShrink: 0 }}>
                                <FileText size={24} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, color: '#111827', fontSize: '0.9rem' }}>{file.name}</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatFileSize(file.size)}</div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setImportFiles((prev) => prev.filter((_, idx) => idx !== i));
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  background: '#fff',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                                aria-label={`Remove ${file.name}`}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap', gap: '8px' }}>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={handleParseDocuments}
                        disabled={importFiles.length === 0}
                      >
                        Extract data
                      </button>
                    </div>
                    {importFiles.length > 0 && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '12px', marginBottom: 0 }}>
                        {importFiles.length} file(s) selected. Click &quot;Extract data&quot; to start.
                      </p>
                    )}
                    {parseError && (
                      <div className="error-message" style={{ marginTop: '12px' }}>{parseError}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      case 'basics': {
        const basicsParsedStyle = (key: string) => getParsedFieldInputStyle(getSourceForFormField(parseResult, key, (form as Record<string, unknown>)[key]), undefined);
        const basicsRevert = (key: string) => {
          const v = getParsedValueForFormKey(parseResult, key);
          setForm((prev) => ({ ...prev, [key]: v }));
        };
        const basicsSource = (key: string) => getSourceForFormField(parseResult, key, (form as Record<string, unknown>)[key]);
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Captured via</label>
                <select
                  value={form.capturedViaEnum || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, capturedViaEnum: e.target.value || undefined }))}
                >
                  <option value="">Select...</option>
                  {dictionaries.capturedVia.length > 0
                    ? dictionaries.capturedVia.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="doctor_visit" value="doctor_visit">Doctor visit</option>,
                        <option key="tele_consult" value="tele_consult">Tele consult</option>,
                        <option key="chat" value="chat">Chat</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              <div className="np-field">
                <label>Gender <span style={{ color: '#e30412' }}>*</span></label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={form.gender || ''}
                    onChange={(e) => {
                      const gender = e.target.value || undefined;
                      const isPregnant = gender === 'female' ? form.isPregnant : undefined;
                      const isLactating = gender === 'female' ? form.isLactating : undefined;
                      setForm((prev) => ({ ...prev, gender, isPregnant, isLactating }));
                      if (gender) setValidationErrors((prev) => ({ ...prev, basics: prev.basics.filter(x => !x.includes('Gender')) }));
                    }}
                    style={validationErrors.basics.some(e => e.includes('Gender')) ? { borderColor: '#e30412' } : basicsParsedStyle('gender')}
                    className={getParsedFieldInputClassName(basicsSource('gender')) || undefined}
                  >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  </select>
                  {basicsSource('gender') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('gender')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
                {validationErrors.basics.some(e => e.includes('Gender')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Gender is required
                  </span>
                )}
              </div>
              {form.gender === 'female' && (
                <>
                  <div className="np-field">
                    <label>Pregnant?</label>
                    <select
                      value={
                        form.isPregnant === undefined || form.isPregnant === null
                          ? ''
                          : form.isPregnant
                          ? 'yes'
                          : 'no'
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          isPregnant:
                            e.target.value === ''
                              ? undefined
                              : e.target.value === 'yes',
                          // Clear lactating if pregnant (typically mutually exclusive)
                          isLactating: e.target.value === 'yes' ? false : prev.isLactating,
                        }))
                      }
                    >
                      <option value="">Unknown</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {!form.isPregnant && (
                    <div className="np-field">
                      <label>Lactating/Breastfeeding?</label>
                      <select
                        value={
                          form.isLactating === undefined || form.isLactating === null
                            ? ''
                            : form.isLactating
                            ? 'yes'
                            : 'no'
                        }
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            isLactating:
                              e.target.value === ''
                                ? undefined
                                : e.target.value === 'yes',
                          }))
                        }
                      >
                        <option value="">Unknown</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  )}
                </>
              )}
              <div className="np-field">
                <DateOfBirthPicker
                  label="Date of Birth"
                  value={form.dateOfBirth}
                  onChange={(dob) => {
                    const derivedLifeStage = dob ? deriveLifeStageFromDOB(dob) : undefined;
                    setForm((prev) => ({
                      ...prev,
                      dateOfBirth: dob,
                      lifeStage: derivedLifeStage,
                    }));
                    // Clear validation error for this field
                    if (dob) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        basics: prev.basics.filter(e => !e.includes('Date of Birth')),
                      }));
                    }
                  }}
                  required
                  error={validationErrors.basics.some(e => e.includes('Date of Birth'))}
                />
                {validationErrors.basics.some(e => e.includes('Date of Birth')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Date of Birth is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Height (cm) <span style={{ color: '#e30412' }}>*</span></label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={form.heightCm ?? ''}
                    placeholder="e.g. 170"
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      setForm((prev) => ({ ...prev, heightCm: value }));
                      if (value && value > 0) setValidationErrors((prev) => ({ ...prev, basics: prev.basics.filter(x => !x.includes('Height')) }));
                    }}
                    style={validationErrors.basics.some(e => e.includes('Height')) ? { borderColor: '#e30412' } : basicsParsedStyle('heightCm')}
                    className={getParsedFieldInputClassName(basicsSource('heightCm')) || undefined}
                  />
                  {basicsSource('heightCm') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('heightCm')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
                {validationErrors.basics.some(e => e.includes('Height')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Height is required and must be greater than 0
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Body weight (kg) <span style={{ color: '#e30412' }}>*</span></label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={form.bodyWeightKg ?? ''}
                    placeholder="e.g. 72"
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      setForm((prev) => ({ ...prev, bodyWeightKg: value }));
                      if (value && value > 0) setValidationErrors((prev) => ({ ...prev, basics: prev.basics.filter(x => !x.includes('Body weight')) }));
                    }}
                    style={validationErrors.basics.some(e => e.includes('Body weight')) ? { borderColor: '#e30412' } : basicsParsedStyle('bodyWeightKg')}
                    className={getParsedFieldInputClassName(basicsSource('bodyWeightKg')) || undefined}
                  />
                  {basicsSource('bodyWeightKg') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('bodyWeightKg')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
                {validationErrors.basics.some(e => e.includes('Body weight')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Body weight is required and must be greater than 0
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Waist circumference (cm)</label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={form.waistCm ?? ''}
                    placeholder="e.g. 80"
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : undefined;
                      setForm((prev) => ({ ...prev, waistCm: value }));
                      setValidationErrors((prev) => ({ ...prev, basics: prev.basics.filter(x => !x.includes('Waist')) }));
                    }}
                    style={basicsParsedStyle('waistCm')}
                    className={getParsedFieldInputClassName(basicsSource('waistCm')) || undefined}
                  />
                  {basicsSource('waistCm') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('waistCm')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
              </div>
              <div className="np-field">
                <label>Hip circumference (cm)</label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={form.hipCm ?? ''}
                    placeholder="e.g. 95"
                    onChange={(e) => setForm((prev) => ({ ...prev, hipCm: e.target.value ? Number(e.target.value) : undefined }))}
                    style={basicsParsedStyle('hipCm')}
                    className={getParsedFieldInputClassName(basicsSource('hipCm')) || undefined}
                  />
                  {basicsSource('hipCm') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('hipCm')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
              </div>
              <div className="np-field">
                <label>Neck circumference (cm)</label>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    value={form.neckCm ?? ''}
                    placeholder="e.g. 38"
                    onChange={(e) => setForm((prev) => ({ ...prev, neckCm: e.target.value ? Number(e.target.value) : undefined }))}
                    style={basicsParsedStyle('neckCm')}
                    className={getParsedFieldInputClassName(basicsSource('neckCm')) || undefined}
                  />
                  {basicsSource('neckCm') === 'edited' && (
                    <button type="button" onClick={() => basicsRevert('neckCm')} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
              </div>
              <div className="np-field">
                <label>Life stage</label>
                <input
                  type="text"
                  value={form.lifeStage || ''}
                  readOnly
                  placeholder="Derived from date of birth"
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="np-field">
                <label>Mother tongue <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={form.motherTongue || ''}
                  onChange={(e) => {
                    const value = e.target.value || undefined;
                    setForm((prev) => ({ ...prev, motherTongue: value }));
                    // Clear validation error for this field
                    if (value) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        basics: prev.basics.filter(e => !e.includes('Mother tongue')),
                      }));
                    }
                  }}
                  style={validationErrors.basics.some(e => e.includes('Mother tongue')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  <option value="hindi">Hindi</option>
                  <option value="tamil">Tamil</option>
                  <option value="telugu">Telugu</option>
                  <option value="bengali">Bengali</option>
                  <option value="marathi">Marathi</option>
                  <option value="gujarati">Gujarati</option>
                  <option value="kannada">Kannada</option>
                  <option value="malayalam">Malayalam</option>
                  <option value="punjabi">Punjabi</option>
                  <option value="odia">Odia</option>
                  <option value="assamese">Assamese</option>
                  <option value="urdu">Urdu</option>
                  <option value="sanskrit">Sanskrit</option>
                  <option value="english">English</option>
                  <option value="other">Other</option>
                </select>
                {validationErrors.basics.some(e => e.includes('Mother tongue')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Mother tongue is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Marital status <span style={{ color: '#e30412' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={form.humanContextJson?.maritalStatus || ''}
                    onChange={(e) => {
                      const value = e.target.value || undefined;
                      setForm((prev) => ({
                        ...prev,
                        humanContextJson: {
                          ...emptyHumanContext(),
                          ...(prev.humanContextJson || {}),
                          maritalStatus: value,
                        } as any,
                      }));
                      if (value) {
                        setValidationErrors((prev) => ({ ...prev, basics: prev.basics.filter(e => !e.includes('Marital status')) }));
                      }
                    }}
                    style={{
                      ...getParsedFieldInputStyle(getSourceForFormField(parseResult, 'maritalStatus', form.humanContextJson?.maritalStatus)),
                      ...(validationErrors.basics.some(e => e.includes('Marital status')) ? { borderColor: '#e30412' } : {}),
                    }}
                    className={getParsedFieldInputClassName(getSourceForFormField(parseResult, 'maritalStatus', form.humanContextJson?.maritalStatus)) || undefined}
                  >
                    <option value="">Select...</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="separated">Separated</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                  {getSourceForFormField(parseResult, 'maritalStatus', form.humanContextJson?.maritalStatus) === 'edited' && (
                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, humanContextJson: { ...emptyHumanContext(), ...(prev.humanContextJson || {}), maritalStatus: String(getParsedValueForFormKey(parseResult, 'maritalStatus') ?? '') } as any }))} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
                {validationErrors.basics.some(e => e.includes('Marital status')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Marital status is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Motivation readiness <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={form.motivationReadiness || ''}
                  onChange={(e) => {
                    const value = e.target.value || undefined;
                    setForm((prev) => ({ ...prev, motivationReadiness: value }));
                    // Clear validation error for this field
                    if (value) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        basics: prev.basics.filter(e => !e.includes('Motivation readiness')),
                      }));
                    }
                  }}
                  style={validationErrors.basics.some(e => e.includes('Motivation readiness')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.motivationReadiness.length > 0
                    ? dictionaries.motivationReadiness.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="low" value="low">Low</option>,
                        <option key="medium" value="medium">Medium</option>,
                        <option key="high" value="high">High</option>,
                      ]}
                </select>
                {validationErrors.basics.some(e => e.includes('Motivation readiness')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Motivation readiness is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Previous diet attempts</label>
                <input
                  type="number"
                  value={form.previousDietAttempts ?? 0}
                  min={0}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      previousDietAttempts: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Support system available?</label>
                <select
                  value={
                    form.supportSystemAvailable === undefined
                      ? ''
                      : form.supportSystemAvailable
                        ? 'yes'
                        : 'no'
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      supportSystemAvailable: v === '' ? undefined : v === 'yes',
                    }));
                  }}
                >
                  <option value="">Unknown</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="np-field">
                <label>Technology comfort level <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={form.technologyComfortLevel || ''}
                  onChange={(e) => {
                    const value = e.target.value || undefined;
                    setForm((prev) => ({ ...prev, technologyComfortLevel: value }));
                    // Clear validation error for this field
                    if (value) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        basics: prev.basics.filter(e => !e.includes('Technology comfort level')),
                      }));
                    }
                  }}
                  style={validationErrors.basics.some(e => e.includes('Technology comfort level')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.technologyComfortLevel.length > 0
                    ? dictionaries.technologyComfortLevel.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="low" value="low">Low</option>,
                        <option key="medium" value="medium">Medium</option>,
                        <option key="high" value="high">High</option>,
                      ]}
                </select>
                {validationErrors.basics.some(e => e.includes('Technology comfort level')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Technology comfort level is required
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'preferences':
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Types of diet <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={preferences.typesOfDiet || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        typesOfDiet: e.target.value || null,
                        typesOfDietNotes: e.target.value !== 'other' ? null : (prev.preferencesJson?.typesOfDietNotes || null),
                      },
                    }))
                  }
                  style={validationErrors.preferences.some(e => e.includes('Types of diet') && !e.includes('notes')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.dietType.length > 0
                    ? dictionaries.dietType.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="vegetarian" value="vegetarian">Vegetarian</option>,
                        <option key="non_vegetarian" value="non_vegetarian">Non Vegetarian</option>,
                        <option key="vegan" value="vegan">Vegan</option>,
                        <option key="keto" value="keto">Keto</option>,
                        <option key="paleo" value="paleo">Paleo</option>,
                        <option key="mediterranean" value="mediterranean">Mediterranean</option>,
                        <option key="low_carb" value="low_carb">Low Carb</option>,
                        <option key="low_fat" value="low_fat">Low Fat</option>,
                        <option key="jain" value="jain">Jain</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.preferences.some(e => e.includes('Types of diet') && !e.includes('notes')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Types of diet is required
                  </span>
                )}
              </div>
              {preferences.typesOfDiet === 'other' && (
                <div className="np-field np-field--full">
                  <label>Types of diet notes <span style={{ color: '#e30412' }}>*</span></label>
                  <input
                    type="text"
                    placeholder="Write here..."
                    value={preferences.typesOfDietNotes || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        preferencesJson: {
                          ...(prev.preferencesJson || emptyPreferences()),
                          typesOfDietNotes: e.target.value || null,
                        },
                      }))
                    }
                    style={validationErrors.preferences.some(e => e.includes('Types of diet notes')) ? { borderColor: '#e30412' } : {}}
                  />
                  {validationErrors.preferences.some(e => e.includes('Types of diet notes')) && (
                    <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                      Types of diet notes is required when "Other" is selected
                    </span>
                  )}
                </div>
              )}
              <div className="np-field np-field--full">
                <label>Favorite food</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={preferences.favoriteFood || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        favoriteFood: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field np-field--full">
                <label>Disliked food</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={preferences.dislikedFood || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        dislikedFood: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Allergies</label>
                <select
                  value={preferences.allergies || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        allergies: e.target.value || null,
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.allergyType.length > 0
                    ? dictionaries.allergyType.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="peanuts" value="peanuts">Peanuts</option>,
                        <option key="tree_nuts" value="tree_nuts">Tree Nuts</option>,
                        <option key="dairy" value="dairy">Dairy</option>,
                        <option key="eggs" value="eggs">Eggs</option>,
                        <option key="fish" value="fish">Fish</option>,
                        <option key="shellfish" value="shellfish">Shellfish</option>,
                        <option key="soy" value="soy">Soy</option>,
                        <option key="wheat" value="wheat">Wheat</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              <div className="np-field np-field--full">
                <label>Allergies notes</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={preferences.allergiesNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        allergiesNotes: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Food intolerances</label>
                <select
                  value={preferences.foodIntolerances || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        foodIntolerances: e.target.value || null,
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.foodIntolerance.length > 0
                    ? dictionaries.foodIntolerance.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="lactose" value="lactose">Lactose</option>,
                        <option key="gluten" value="gluten">Gluten</option>,
                        <option key="fructose" value="fructose">Fructose</option>,
                        <option key="histamine" value="histamine">Histamine</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              <div className="np-field np-field--full">
                <label>Food intolerances notes</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={preferences.foodIntolerancesNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        foodIntolerancesNotes: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Nutritional deficiencies</label>
                <select
                  value={preferences.nutritionalDeficiencies || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        nutritionalDeficiencies: e.target.value || null,
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.nutritionalDeficiency.length > 0
                    ? dictionaries.nutritionalDeficiency.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="vitamin_d" value="vitamin_d">Vitamin D</option>,
                        <option key="vitamin_b12" value="vitamin_b12">Vitamin B12</option>,
                        <option key="iron" value="iron">Iron</option>,
                        <option key="calcium" value="calcium">Calcium</option>,
                        <option key="magnesium" value="magnesium">Magnesium</option>,
                        <option key="zinc" value="zinc">Zinc</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              <div className="np-field np-field--full">
                <label>Nutritional deficiencies notes</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={preferences.nutritionalDeficienciesNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        nutritionalDeficienciesNotes: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Water intake <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={preferences.waterIntake || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        waterIntake: e.target.value || null,
                      },
                    }))
                  }
                  style={validationErrors.preferences.some(e => e.includes('Water intake')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select an option</option>
                  {dictionaries.waterIntake.length > 0
                    ? dictionaries.waterIntake.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="less_than_2_liters" value="less_than_2_liters">Less than 2 liters/day</option>,
                        <option key="2_4_liters" value="2_4_liters">2-4 liters/day</option>,
                        <option key="more_than_4_liters" value="more_than_4_liters">More than 4 liters/day</option>,
                      ]}
                </select>
                {validationErrors.preferences.some(e => e.includes('Water intake')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Water intake is required
                  </span>
                )}
              </div>
              <div className="np-field np-field--full">
                <label>Other information</label>
                <textarea
                  rows={4}
                  placeholder="Write here..."
                  value={preferences.otherInformation || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferencesJson: {
                        ...(prev.preferencesJson || emptyPreferences()),
                        otherInformation: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        );

      case 'medical_history':
        const medicalHistory = form.medicalHistoryJson || emptyMedicalHistory();
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Diseases</label>
                <select
                  value={medicalHistory.diseases || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        diseases: e.target.value || null,
                        diseasesNotes: e.target.value !== 'other' ? null : (prev.medicalHistoryJson?.diseasesNotes || null),
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.diseases.length > 0
                    ? dictionaries.diseases.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="diabetes" value="diabetes">Diabetes</option>,
                        <option key="hypertension" value="hypertension">Hypertension</option>,
                        <option key="heart_disease" value="heart_disease">Heart Disease</option>,
                        <option key="thyroid" value="thyroid">Thyroid</option>,
                        <option key="pcos" value="pcos">PCOS</option>,
                        <option key="ibs" value="ibs">IBS</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              {medicalHistory.diseases === 'other' && (
                <div className="np-field np-field--full">
                  <label>Diseases notes</label>
                  <input
                    type="text"
                    placeholder="Write here..."
                    value={medicalHistory.diseasesNotes || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        medicalHistoryJson: {
                          ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                          diseasesNotes: e.target.value || null,
                        },
                      }))
                    }
                  />
                </div>
              )}
              <div className="np-field np-field--full">
                <label>Diseases notes</label>
                <input
                  type="text"
                  placeholder="Write here..."
                  value={medicalHistory.diseasesNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        diseasesNotes: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              <div className="np-field">
                <label>Medication</label>
                <select
                  value={medicalHistory.medication || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        medication: e.target.value || null,
                        medicationNotes: e.target.value !== 'other' ? null : (prev.medicalHistoryJson?.medicationNotes || null),
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.medication.length > 0
                    ? dictionaries.medication.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="metformin" value="metformin">Metformin</option>,
                        <option key="aspirin" value="aspirin">Aspirin</option>,
                        <option key="thyroid_medication" value="thyroid_medication">Thyroid Medication</option>,
                        <option key="blood_pressure_medication" value="blood_pressure_medication">Blood Pressure Medication</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              {medicalHistory.medication === 'other' && (
                <div className="np-field np-field--full">
                  <label>Medication details</label>
                <input
                  type="text"
                    placeholder="Write here..."
                    value={medicalHistory.medicationNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                        medicalHistoryJson: {
                          ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                          medicationNotes: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
              )}
              <div className="np-field">
                <label>Personal history</label>
                <select
                  value={medicalHistory.personalHistory || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        personalHistory: e.target.value || null,
                        personalHistoryNotes: e.target.value !== 'other' ? null : (prev.medicalHistoryJson?.personalHistoryNotes || null),
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.personalHistory.length > 0
                    ? dictionaries.personalHistory.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="surgery" value="surgery">Previous Surgery</option>,
                        <option key="injury" value="injury">Previous Injury</option>,
                        <option key="hospitalization" value="hospitalization">Previous Hospitalization</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              {medicalHistory.personalHistory === 'other' && (
                <div className="np-field np-field--full">
                  <label>Personal history details</label>
                <input
                  type="text"
                    placeholder="Write here..."
                    value={medicalHistory.personalHistoryNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                        medicalHistoryJson: {
                          ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                          personalHistoryNotes: e.target.value || null,
                        },
                    }))
                  }
                />
              </div>
              )}
              <div className="np-field">
                <label>Family history</label>
                <select
                  value={medicalHistory.familyHistory || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        familyHistory: e.target.value || null,
                        familyHistoryNotes: e.target.value !== 'other' ? null : (prev.medicalHistoryJson?.familyHistoryNotes || null),
                      },
                    }))
                  }
                >
                  <option value="">Select...</option>
                  {dictionaries.familyHistory.length > 0
                    ? dictionaries.familyHistory.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="diabetes" value="diabetes">Diabetes</option>,
                        <option key="hypertension" value="hypertension">Hypertension</option>,
                        <option key="heart_disease" value="heart_disease">Heart Disease</option>,
                        <option key="cancer" value="cancer">Cancer</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
              </div>
              {medicalHistory.familyHistory === 'other' && (
                <div className="np-field np-field--full">
                  <label>Family history details</label>
                <input
                  type="text"
                    placeholder="Write here..."
                    value={medicalHistory.familyHistoryNotes || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                        medicalHistoryJson: {
                          ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                          familyHistoryNotes: e.target.value || null,
                        },
                    }))
                  }
                />
                </div>
              )}
              <div className="np-field np-field--full">
                <label>Other information</label>
                <textarea
                  rows={4}
                  placeholder="Write here..."
                  value={medicalHistory.otherInformation || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      medicalHistoryJson: {
                        ...(prev.medicalHistoryJson || emptyMedicalHistory()),
                        otherInformation: e.target.value || null,
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        );

      case 'human_context':
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Activity level <span style={{ color: '#e30412' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <select
                    value={humanContext.activityLevel || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        humanContextJson: {
                          ...(prev.humanContextJson || emptyHumanContext()),
                          activityLevel: e.target.value || undefined,
                        } as any,
                      }))
                    }
                    style={{
                      ...getParsedFieldInputStyle(getSourceForFormField(parseResult, 'activityLevel', humanContext.activityLevel)),
                      ...(validationErrors.human_context.some(e => e.includes('Activity level')) ? { borderColor: '#e30412' } : {}),
                    }}
                    className={getParsedFieldInputClassName(getSourceForFormField(parseResult, 'activityLevel', humanContext.activityLevel)) || undefined}
                  >
                    <option value="">Select...</option>
                    {dictionaries.activityLevel.length > 0
                      ? dictionaries.activityLevel.map((item) => (
                          <option key={item.id} value={item.code}>
                            {item.display_name}
                          </option>
                        ))
                      : [
                          <option key="sedentary" value="sedentary">Sedentary</option>,
                          <option key="light" value="light">Lightly active</option>,
                          <option key="moderate" value="moderate">Moderately active</option>,
                          <option key="active" value="active">Active</option>,
                          <option key="very_active" value="very_active">Very active</option>,
                        ]}
                  </select>
                  {getSourceForFormField(parseResult, 'activityLevel', humanContext.activityLevel) === 'edited' && (
                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, humanContextJson: { ...(prev.humanContextJson || emptyHumanContext()), activityLevel: String(getParsedValueForFormKey(parseResult, 'activityLevel') ?? '') } as any }))} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Revert</button>
                  )}
                </div>
                {validationErrors.human_context.some(e => e.includes('Activity level')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Activity level is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Sleep quality <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.sleepQuality || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...emptyHumanContext(),
                        ...(prev.humanContextJson || {}),
                        sleepQuality: e.target.value || undefined,
                      } as any,
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Sleep quality')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.sleepQuality.length > 0
                    ? dictionaries.sleepQuality.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="5_hours" value="5_hours">5 hours/night</option>,
                        <option key="6_hours" value="6_hours">6 hours/night</option>,
                        <option key="7_hours" value="7_hours">7 hours/night</option>,
                        <option key="8_hours" value="8_hours">8 hours/night</option>,
                        <option key="9_hours" value="9_hours">9 hours/night</option>,
                        <option key="10_hours" value="10_hours">10 hours/night</option>,
                        <option key="11_hours" value="11_hours">11 hours/night</option>,
                        <option key="12_hours" value="12_hours">12 hours/night</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Sleep quality')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Sleep quality is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Sleep patterns <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.sleepPatterns || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...(prev.humanContextJson || emptyHumanContext()),
                        sleepPatterns: e.target.value || null,
                      } as any,
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Sleep patterns')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.sleepPattern.length > 0
                    ? dictionaries.sleepPattern.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="regular" value="regular">Regular schedule</option>,
                        <option key="irregular" value="irregular">Irregular schedule</option>,
                        <option key="shift_work" value="shift_work">Shift work schedule</option>,
                        <option key="night_owl" value="night_owl">Night owl (late to bed)</option>,
                        <option key="early_bird" value="early_bird">Early bird (early to bed)</option>,
                        <option key="fragmented" value="fragmented">Fragmented sleep</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Sleep patterns')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Sleep patterns is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Culture / identity <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.cultureIdentity || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: { ...(prev.humanContextJson || emptyHumanContext()), cultureIdentity: e.target.value || undefined },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Culture identity')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.cultureIdentity.length > 0
                    ? dictionaries.cultureIdentity.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="south_indian" value="south_indian">South Indian</option>,
                        <option key="north_indian" value="north_indian">North Indian</option>,
                        <option key="east_indian" value="east_indian">East Indian</option>,
                        <option key="west_indian" value="west_indian">West Indian</option>,
                        <option key="northeast_indian" value="northeast_indian">Northeast Indian</option>,
                        <option key="central_indian" value="central_indian">Central Indian</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Culture identity')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Culture identity is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Religious restrictions <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.religiousRestrictions?.[0] || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...(prev.humanContextJson || emptyHumanContext()),
                        religiousRestrictions: e.target.value ? [e.target.value] : [],
                      },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Religious restrictions')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.religiousRestriction.length > 0
                    ? dictionaries.religiousRestriction.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="none" value="none">None</option>,
                        <option key="halal" value="halal">Halal</option>,
                        <option key="kosher" value="kosher">Kosher</option>,
                        <option key="vegetarian" value="vegetarian">Vegetarian</option>,
                        <option key="vegan" value="vegan">Vegan</option>,
                        <option key="jain" value="jain">Jain</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Religious restrictions')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Religious restrictions is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Work schedule <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.workSchedule || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: { ...(prev.humanContextJson || emptyHumanContext()), workSchedule: e.target.value || undefined },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Work schedule')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.workSchedule.length > 0
                    ? dictionaries.workSchedule.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="regular_9to5" value="regular_9to5">Regular 9-5</option>,
                        <option key="shift_work" value="shift_work">Shift work</option>,
                        <option key="night_shifts" value="night_shifts">Night shifts</option>,
                        <option key="flexible" value="flexible">Flexible hours</option>,
                        <option key="irregular" value="irregular">Irregular schedule</option>,
                        <option key="unemployed" value="unemployed">Unemployed</option>,
                        <option key="retired" value="retired">Retired</option>,
                        <option key="student" value="student">Student</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Work schedule')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Work schedule is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Stress levels <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.stressLevels || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: { ...(prev.humanContextJson || emptyHumanContext()), stressLevels: e.target.value },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Stress levels')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select</option>
                  {dictionaries.stressLevel.length > 0
                    ? dictionaries.stressLevel.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="low" value="low">Low</option>,
                        <option key="medium" value="medium">Medium</option>,
                        <option key="high" value="high">High</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Stress levels')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Stress levels is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Family structure <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.familyStructure || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: { ...(prev.humanContextJson || emptyHumanContext()), familyStructure: e.target.value || undefined },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Family structure')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.familyStructure.length > 0
                    ? dictionaries.familyStructure.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="single" value="single">Single</option>,
                        <option key="married_no_children" value="married_no_children">Married, no children</option>,
                        <option key="married_with_children" value="married_with_children">Married with children</option>,
                        <option key="single_parent" value="single_parent">Single parent</option>,
                        <option key="extended_family" value="extended_family">Extended family</option>,
                        <option key="living_with_parents" value="living_with_parents">Living with parents</option>,
                        <option key="other" value="other">Other</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Family structure')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Family structure is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Cooking responsibility <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.cookingResponsibility || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...(prev.humanContextJson || emptyHumanContext()),
                        cookingResponsibility: e.target.value,
                      },
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Cooking responsibility')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select</option>
                  {dictionaries.cookingResponsibility.length > 0
                    ? dictionaries.cookingResponsibility.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="self" value="self">Self</option>,
                        <option key="family" value="family">Family</option>,
                        <option key="helper" value="helper">Helper</option>,
                        <option key="restaurant" value="restaurant">Mostly outside</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Cooking responsibility')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Cooking responsibility is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Smoking status <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.smokingStatus || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...emptyHumanContext(),
                        ...(prev.humanContextJson || {}),
                        smokingStatus: e.target.value || undefined,
                      } as any,
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Smoking status')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.smokingStatus.length > 0
                    ? dictionaries.smokingStatus.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="no" value="no">No</option>,
                        <option key="yes" value="yes">Yes</option>,
                        <option key="occasional" value="occasional">Occasional</option>,
                        <option key="former" value="former">Former smoker</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Smoking status')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Smoking status is required
                  </span>
                )}
              </div>
              <div className="np-field">
                <label>Alcohol consumption <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={humanContext.alcoholConsumption || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      humanContextJson: {
                        ...emptyHumanContext(),
                        ...(prev.humanContextJson || {}),
                        alcoholConsumption: e.target.value || undefined,
                      } as any,
                    }))
                  }
                  style={validationErrors.human_context.some(e => e.includes('Alcohol consumption')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select...</option>
                  {dictionaries.alcoholConsumption.length > 0
                    ? dictionaries.alcoholConsumption.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="no" value="no">No</option>,
                        <option key="occasional" value="occasional">Occasional</option>,
                        <option key="regular" value="regular">Regular</option>,
                      ]}
                </select>
                {validationErrors.human_context.some(e => e.includes('Alcohol consumption')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Alcohol consumption is required
                  </span>
                )}
              </div>
            </div>
          </div>
        );

      case 'constraints':
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Monthly budget (optional)</label>
                <input
                  type="number"
                  placeholder="e.g. 6000"
                  value={(constraints.budget as any)?.monthly_budget ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      constraintsJson: {
                        ...(prev.constraintsJson || emptyConstraints()),
                        budget: {
                          ...(((prev.constraintsJson || emptyConstraints()).budget as any) || {}),
                          monthly_budget: e.target.value ? Number(e.target.value) : undefined,
                        },
                      },
                    }))
                  }
                />
              </div>

              <div className="np-field">
                <label>Currency</label>
                <input
                  type="text"
                  value={(constraints.budget as any)?.currency || 'INR'}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      constraintsJson: {
                        ...(prev.constraintsJson || emptyConstraints()),
                        budget: {
                          ...(((prev.constraintsJson || emptyConstraints()).budget as any) || {}),
                          currency: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </div>

              <div className="np-field">
                <label>Work hours (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 9–6"
                  value={(constraints.schedule as any)?.work_hours || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      constraintsJson: {
                        ...(prev.constraintsJson || emptyConstraints()),
                        schedule: {
                          ...(((prev.constraintsJson || emptyConstraints()).schedule as any) || {}),
                          work_hours: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </div>

              <div className="np-field">
                <label>Meal prep time (minutes)</label>
                <input
                  type="number"
                  value={(constraints.schedule as any)?.meal_prep_time_min ?? 30}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      constraintsJson: {
                        ...(prev.constraintsJson || emptyConstraints()),
                        schedule: {
                          ...(((prev.constraintsJson || emptyConstraints()).schedule as any) || {}),
                          meal_prep_time_min: Number(e.target.value) || 30,
                        },
                      },
                    }))
                  }
                />
              </div>

              <div className="np-field np-field--full">
                <label>Won't do (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g. calorie counting, cooking"
                  value={joinCsv(constraints.wontDo)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      constraintsJson: {
                        ...(prev.constraintsJson || emptyConstraints()),
                        wontDo: splitCsv(e.target.value),
                      },
                    }))
                  }
                />
              </div>

              <div className="np-field np-field--full" style={{ marginTop: '16px' }}>
                <label style={{ marginBottom: '8px', display: 'block' }}>Cooking facilities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  {[
                    { key: 'has_stove', label: 'Stove' },
                    { key: 'has_microwave', label: 'Microwave' },
                    { key: 'has_refrigerator', label: 'Refrigerator' },
                    { key: 'has_oven', label: 'Oven' },
                  ].map(({ key, label }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(constraints.cooking as any)?.[key] ?? false}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            constraintsJson: {
                              ...(prev.constraintsJson || emptyConstraints()),
                              cooking: {
                                ...(((prev.constraintsJson || emptyConstraints()).cooking as any) || {}),
                                [key]: e.target.checked,
                              },
                            },
                          }))
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'meal_pattern':
        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="np-grid">
              <div className="np-field">
                <label>Typical meals per day <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={mealPattern.typicalMealsPerDay ?? ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : undefined;
                    setForm((prev) => {
                      const prevMealPattern = prev.mealPatternJson || emptyMealPattern();
                      const currentMealTimes = { ...(prevMealPattern.mealTimes || {}) };
                      
                      // Initialize default meal times for all slots if not already set
                      if (value) {
                        for (let i = 0; i < value; i++) {
                          const defaultMealType = DEFAULT_MEAL_TYPE_ORDER[i] ?? 'breakfast';
                          const defaultTime = DEFAULT_MEAL_TIMES[i] ?? '12:00';
                          // Only set if not already present
                          if (!currentMealTimes[defaultMealType]) {
                            currentMealTimes[defaultMealType] = defaultTime;
                          }
                          // Initialize meal type selection
                          if (!mealTypeSelections[i]) {
                            setMealTypeSelections((prevSelections) => ({
                              ...prevSelections,
                              [i]: defaultMealType,
                            }));
                          }
                        }
                      }
                      
                      return {
                      ...prev,
                      mealPatternJson: {
                          ...prevMealPattern,
                        typicalMealsPerDay: value,
                          mealTimes: currentMealTimes,
                      },
                      };
                    });
                    // Clear validation error for this field
                    if (value && value >= 1) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        meal_pattern: prev.meal_pattern.filter(e => !e.includes('Typical meals per day')),
                      }));
                    }
                  }}
                  style={validationErrors.meal_pattern.some(e => e.includes('Typical meals per day')) ? { borderColor: '#e30412' } : {}}
                >
                  <option value="">Select</option>
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? 'meal' : 'meals'}
                    </option>
                  ))}
                </select>
                {validationErrors.meal_pattern.some(e => e.includes('Typical meals per day')) && (
                  <span style={{ color: '#e30412', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                    Typical meals per day is required and must be at least 1
                  </span>
                )}
              </div>

              {/* Dynamic meal time selectors based on meals per day */}
              {Array.from({ length: mealPattern.typicalMealsPerDay || 0 }, (_, index) => {
                const defaultMealType = DEFAULT_MEAL_TYPE_ORDER[index] ?? 'breakfast';
                const defaultTime = DEFAULT_MEAL_TIMES[index] ?? '12:00';
                
                const mealTimesObj = mealPattern.mealTimes || {};
                
                // Get the selected meal type for this slot from state, or use default
                const mealType = mealTypeSelections[index] || defaultMealType;
                // Get the time for the selected meal type
                const currentTime = mealTimesObj[mealType] || defaultTime;
                
                const timeComponents = parseTimeToComponents(currentTime);
                
                return (
                  <div className="np-field" key={`meal-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label>Meal {index + 1} - Type & Time <span style={{ color: '#e30412' }}>*</span></label>
                    <select
                      value={mealType}
                      onChange={(e) => {
                        const newMealType = e.target.value;
                        if (!newMealType) return;
                        
                        // Update the meal type selection for this slot
                        setMealTypeSelections((prev) => ({
                          ...prev,
                          [index]: newMealType,
                        }));
                        
                        setForm((prev) => {
                          const prevMealPattern = prev.mealPatternJson || emptyMealPattern();
                          const currentMealTimes = { ...(prevMealPattern.mealTimes || {}) };
                          
                          // Get current time for the old meal type
                          const timeToPreserve = currentMealTimes[mealType] || defaultTime;
                          
                          // Remove old meal type if it exists and is different
                          if (mealType && mealType !== newMealType && mealType in currentMealTimes) {
                            delete currentMealTimes[mealType];
                          }
                          
                          // Add new meal type with preserved time
                          currentMealTimes[newMealType] = timeToPreserve;
                          
                          return {
                        ...prev,
                        mealPatternJson: {
                              ...prevMealPattern,
                              mealTimes: currentMealTimes,
                            },
                          };
                        });
                      }}
                    >
                      <option value="">Select meal type</option>
                      {dictionaries.mealType.length > 0
                        ? dictionaries.mealType.map((item) => (
                            <option key={item.id} value={item.code}>
                              {item.display_name}
                            </option>
                          ))
                        : MEAL_TYPES.map((mt) => (
                            <option key={mt.value} value={mt.value}>
                              {mt.label}
                            </option>
                          ))}
                    </select>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>Time <span style={{ color: '#e30412' }}>*</span></div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          value={timeComponents.hour}
                        onChange={(e) => {
                          const newHour = e.target.value;
                          if (!newHour) return;
                          
                          setForm((prev) => {
                            const prevMealPattern = prev.mealPatternJson || emptyMealPattern();
                            const currentMealTimes = { ...(prevMealPattern.mealTimes || {}) };
                            
                            // Use the mealType from closure (what's displayed in the dropdown)
                            const currentTimeForSlot = currentMealTimes[mealType] || defaultTime;
                            const currentTimeComps = parseTimeToComponents(currentTimeForSlot);
                            const newTime = combineTimeComponents(newHour, currentTimeComps.minute, currentTimeComps.ampm);
                            currentMealTimes[mealType] = newTime;
                            
                            return {
                              ...prev,
                              mealPatternJson: {
                                ...prevMealPattern,
                                mealTimes: currentMealTimes,
                              },
                            };
                          });
                        }}
                        style={{ flex: 1 }}
                      >
                        <option value="">Hour</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                          <option key={h} value={String(h)}>
                            {h}
                          </option>
                        ))}
                      </select>
                      <select
                        value={timeComponents.minute}
                        onChange={(e) => {
                          const newMinute = e.target.value;
                          if (!newMinute) return;
                          
                          setForm((prev) => {
                            const prevMealPattern = prev.mealPatternJson || emptyMealPattern();
                            const currentMealTimes = { ...(prevMealPattern.mealTimes || {}) };
                            
                            // Use the mealType from closure
                            const currentTimeForSlot = currentMealTimes[mealType] || defaultTime;
                            const currentTimeComps = parseTimeToComponents(currentTimeForSlot);
                            const newTime = combineTimeComponents(currentTimeComps.hour, newMinute, currentTimeComps.ampm);
                            currentMealTimes[mealType] = newTime;
                            
                            return {
                              ...prev,
                              mealPatternJson: {
                                ...prevMealPattern,
                                mealTimes: currentMealTimes,
                              },
                            };
                          });
                        }}
                        style={{ flex: 1 }}
                      >
                        <option value="">Minute</option>
                        {['00', '15', '30', '45'].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        value={timeComponents.ampm}
                        onChange={(e) => {
                          const newAmpm = e.target.value;
                          if (!newAmpm) return;
                          
                          setForm((prev) => {
                            const prevMealPattern = prev.mealPatternJson || emptyMealPattern();
                            const currentMealTimes = { ...(prevMealPattern.mealTimes || {}) };
                            
                            // Use the mealType from closure
                            const currentTimeForSlot = currentMealTimes[mealType] || defaultTime;
                            const currentTimeComps = parseTimeToComponents(currentTimeForSlot);
                            const newTime = combineTimeComponents(currentTimeComps.hour, currentTimeComps.minute, newAmpm);
                            currentMealTimes[mealType] = newTime;
                            
                            return {
                              ...prev,
                              mealPatternJson: {
                                ...prevMealPattern,
                                mealTimes: currentMealTimes,
                              },
                            };
                          });
                        }}
                        style={{ flex: 1 }}
                      >
                        <option value="">AM/PM</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="np-field">
                <label>Snack frequency <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={mealPattern.snackFrequency || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      mealPatternJson: {
                        ...(prev.mealPatternJson || emptyMealPattern()),
                        snackFrequency: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">Select</option>
                  {dictionaries.snackFrequency.length > 0
                    ? dictionaries.snackFrequency.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="never" value="never">Never</option>,
                        <option key="rarely" value="rarely">Rarely</option>,
                        <option key="sometimes" value="sometimes">Sometimes</option>,
                        <option key="often" value="often">Often</option>,
                      ]}
                </select>
              </div>

              <div className="np-field">
                <label>Late-night eating?</label>
                <select
                  value={mealPattern.lateNightEating ? 'yes' : 'no'}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      mealPatternJson: {
                        ...(prev.mealPatternJson || emptyMealPattern()),
                        lateNightEating: e.target.value === 'yes',
                      },
                    }))
                  }
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              <div className="np-field">
                <label>Weekend pattern <span style={{ color: '#e30412' }}>*</span></label>
                <select
                  value={mealPattern.weekendPatterns || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      mealPatternJson: {
                        ...(prev.mealPatternJson || emptyMealPattern()),
                        weekendPatterns: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">Select</option>
                  {dictionaries.weekendPattern.length > 0
                    ? dictionaries.weekendPattern.map((item) => (
                        <option key={item.id} value={item.code}>
                          {item.display_name}
                        </option>
                      ))
                    : [
                        <option key="same_as_weekday" value="same_as_weekday">Same as weekday</option>,
                        <option key="more_indulgent" value="more_indulgent">More indulgent</option>,
                        <option key="irregular" value="irregular">Irregular</option>,
                      ]}
                </select>
              </div>
            </div>
          </div>
        );

      case 'insights': {
        // Map backend metric_key to parsed-json field so Insights shows values from parsed documents when present.
        const parsedValueForMetric = (metricKey: string): number | null => {
          const p = parseResult;
          if (!p) return null;
          const v = (() => {
            switch (metricKey) {
              case 'bmi':
                return p.bmi ?? (p.body_weight_kg != null && p.height_cm != null && p.height_cm > 0 ? p.body_weight_kg / ((p.height_cm / 100) ** 2) : null);
              case 'bsa':
                if (p.bsa != null && Number.isFinite(p.bsa)) return p.bsa;
                if (p.height_cm != null && p.body_weight_kg != null && p.height_cm > 0 && p.body_weight_kg > 0)
                  return 0.007184 * (p.height_cm ** 0.725) * (p.body_weight_kg ** 0.425);
                return null;
              case 'whtr':
                return (p.waist_cm != null && p.height_cm != null && p.height_cm > 0) ? p.waist_cm / p.height_cm : null;
              case 'whr': return p.whr ?? (p.waist_cm != null && p.hip_cm != null && p.hip_cm > 0 ? p.waist_cm / p.hip_cm : null);
              case 'bai':
                if (p.hip_cm != null && p.height_cm != null && p.height_cm > 0)
                  return (p.hip_cm / ((p.height_cm / 100) ** 1.5)) - 18;
                return null;
              case 'bfp': return p.body_fat_pct ?? null;
              case 'lbm': return null;
              case 'fm': return p.fat_mass_kg ?? (p.body_weight_kg != null && p.body_fat_pct != null ? (p.body_weight_kg * p.body_fat_pct) / 100 : null);
              case 'fat_free_mass': return p.fat_free_body_weight_kg ?? null;
              case 'bmr': return p.bmr_kcal ?? null;
              case 'metabolic_age': return p.metabolic_age ?? p.body_age ?? null;
              case 'body_age': return p.body_age ?? p.metabolic_age ?? null;
              case 'body_score': return p.body_score ?? null;
              case 'vfi': return null;
              case 'body_water_pct': return p.body_water_pct ?? null;
              case 'tbw': return p.body_water_kg ?? null;
              case 'body_water_kg': return p.body_water_kg ?? null;
              case 'intracellular_water_kg': return p.intracellular_water_kg ?? null;
              case 'extracellular_water_kg': return p.extracellular_water_kg ?? null;
              case 'ecw_tbw_ratio': return p.ecw_tbw_ratio ?? null;
              case 'mineral_mass_kg': return p.mineral_mass_kg ?? null;
              case 'phase_angle': return p.phase_angle ?? null;
              case 'bone_mass': return p.bone_mass_kg ?? null;
              case 'bone_mass_kg': return p.bone_mass_kg ?? null;
              case 'muscle_mass_kg': return p.muscle_mass_kg ?? p.skeletal_muscle_kg ?? null;
              case 'skeletal_muscle_kg': return p.skeletal_muscle_kg ?? null;
              case 'skeletal_muscle_pct': return p.skeletal_muscle_pct ?? null;
              case 'protein_pct': return p.protein_pct ?? null;
              case 'protein_mass_kg': return p.protein_mass_kg ?? null;
              case 'subcutaneous_fat_pct': return p.subcutaneous_fat_pct ?? null;
              case 'visceral_fat_level': return p.visceral_fat_grade ?? null;
              case 'visceral_fat_grade': return p.visceral_fat_grade ?? null;
              case 'smi': return p.smi_kg_m2 ?? null;
              case 'smi_kg_m2': return p.smi_kg_m2 ?? null;
              case 'obesity_pct': return p.obesity_pct ?? null;
              default: return null;
            }
          })();
          return v != null && typeof v === 'number' && Number.isFinite(v) ? v : null;
        };
        const insightsItems = (insightsSummary?.items ?? []).filter((item: DerivedMetricSummaryItem) => {
          if (insightsSubTab === 'metrics') return item.category !== 'Composite Score' && !item.category?.toLowerCase().includes('composite');
          return item.category === 'Composite Score' || item.category?.toLowerCase().includes('composite');
        }).sort((a: DerivedMetricSummaryItem, b: DerivedMetricSummaryItem) => a.metric_key.localeCompare(b.metric_key));

        return (
          <div className="np-step">
            <HealthVitalsInsightsView
              items={insightsItems}
              loading={insightsLoading}
              error={insightsError}
              insightsSubTab={insightsSubTab}
              onInsightsSubTabChange={setInsightsSubTab}
              edits={insightsEdits}
              onEditsChange={setInsightsEdits}
              getDisplayValue={(item) => parsedValueForMetric(item.metric_key)}
              showSourceColumn
              onRefresh={fetchInsights}
              refreshLoading={insightsLoading}
              profileJustCreated={profileJustCreated}
            />
          </div>
        );
      }

      case 'review':
        // Format enum values to human-readable text
        const formatEnumValue = (value: string | null | undefined): string | null => {
          if (!value || typeof value !== 'string') return null;
          
          const enumMap: Record<string, string> = {
            // Diet types
            'none': 'None',
            'vegetarian': 'Vegetarian',
            'non_vegetarian': 'Non Vegetarian',
            'vegan': 'Vegan',
            'keto': 'Keto',
            'paleo': 'Paleo',
            'mediterranean': 'Mediterranean',
            'low_carb': 'Low Carb',
            'low_fat': 'Low Fat',
            
            // Allergies
            'peanuts': 'Peanuts',
            'tree_nuts': 'Tree Nuts',
            'dairy': 'Dairy',
            'eggs': 'Eggs',
            'fish': 'Fish',
            'shellfish': 'Shellfish',
            'soy': 'Soy',
            'wheat': 'Wheat',
            
            // Food intolerances
            'lactose': 'Lactose',
            'gluten': 'Gluten',
            'fructose': 'Fructose',
            'histamine': 'Histamine',
            
            // Nutritional deficiencies
            'vitamin_d': 'Vitamin D',
            'vitamin_b12': 'Vitamin B12',
            'iron': 'Iron',
            'calcium': 'Calcium',
            'vitamin_c': 'Vitamin C',
            'zinc': 'Zinc',
            'magnesium': 'Magnesium',
            'folate': 'Folate',
            
            // Water intake
            'less_than_2_liters': 'Less than 2 liters/day',
            '2_4_liters': '2-4 liters/day',
            'more_than_4_liters': 'More than 4 liters/day',
            
            // Diseases
            'diabetes': 'Diabetes',
            'hypertension': 'Hypertension',
            'heart_disease': 'Heart Disease',
            'thyroid': 'Thyroid',
            'pcos': 'PCOS',
            'ibs': 'IBS',
            'crohns': "Crohn's Disease",
            'celiac': 'Celiac Disease',
            
            // Medication
            'metformin': 'Metformin',
            'aspirin': 'Aspirin',
            'thyroid_medication': 'Thyroid Medication',
            'blood_pressure_medication': 'Blood Pressure Medication',
            
            // Personal/Family history
            'diabetes_family': 'Diabetes',
            'heart_disease_family': 'Heart Disease',
            'cancer_family': 'Cancer',
            
            // Activity level
            'sedentary': 'Sedentary',
            'light': 'Lightly Active',
            'moderate': 'Moderately Active',
            
            // Stress levels, Motivation readiness, Technology comfort (shared values)
            'low': 'Low',
            'medium': 'Medium',
            'high': 'High', // Note: For activity level 'high', it should be 'Very Active' but handled separately
            
            // Gender
            'male': 'Male',
            'female': 'Female',
            'other': 'Other',
            
            // Life stage
            'adult': 'Adult',
            'elderly': 'Elderly',
            'pregnant': 'Pregnant',
            'lactating': 'Lactating',
            
            // Marital status
            'single': 'Single',
            'married': 'Married',
            'divorced': 'Divorced',
            'widowed': 'Widowed',
            'separated': 'Separated',
            
            // Captured via
            'doctor_visit': 'Doctor Visit',
            'tele_consult': 'Tele Consult',
            'chat': 'Chat',
            'onboarding': 'Onboarding',
            'self_report': 'Self Report',
          };
          
          return enumMap[value.toLowerCase()] || value.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        };

        const show = (v: any) => {
          if (v === null || v === undefined) return null;
          if (typeof v === 'string') {
            const trimmed = v.trim();
            if (!trimmed) return null;
            return formatEnumValue(trimmed) || trimmed;
          }
          return String(v);
        };

        const showBool = (v: any) => {
          if (v === null || v === undefined) return null;
          return v ? 'Yes' : 'No';
        };

        const showList = (arr?: any[]) => {
          const list = (arr || []).map((x) => (typeof x === 'string' ? x.trim() : String(x))).filter(Boolean);
          if (list.length === 0) return null;
          return list.map(item => formatEnumValue(item) || item).join(', ');
        };

        return (
          <div className="np-step">
            {parseResult && <ParsedLegend />}
            <div className="section-card">
              <h3 className="section-title">Review</h3>
              <p className="np-muted">
                This will save into <code>nutrition_patient_profile</code> and create a new version if a profile already exists.
              </p>

              {profile && (
                <div className="profile-meta-grid">
                  <div>
                    <strong>Current version:</strong> {profile.profileVersion}
                  </div>
                  <div>
                    <strong>Captured on:</strong> {profile.captureDate}
                  </div>
                  <div>
                    <strong>Captured via:</strong> {profile.capturedViaEnum}
                  </div>
                </div>
              )}

              {(() => {
                const medicalHistory = form.medicalHistoryJson || emptyMedicalHistory();

                return (
                  <div className="np-summary">
                    <div className="np-summary-section">
                      <h4>Basics</h4>
                      <div className="np-summary-grid">
                        <div className="np-summary-item">
                          <div className="np-summary-label">Captured via</div>
                          <div className="np-summary-value">{show(form.capturedViaEnum)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Gender</div>
                          <div className="np-summary-value">{show(form.gender)}</div>
                        </div>
                        {form.gender === 'female' && (
                          <>
                            <div className="np-summary-item">
                              <div className="np-summary-label">Pregnant?</div>
                              <div className="np-summary-value">{showBool(form.isPregnant)}</div>
                            </div>
                            <div className="np-summary-item">
                              <div className="np-summary-label">Lactating/Breastfeeding?</div>
                              <div className="np-summary-value">{showBool(form.isLactating)}</div>
                            </div>
                          </>
                        )}
                        <div className="np-summary-item">
                          <div className="np-summary-label">Date of Birth</div>
                          <div className="np-summary-value">{show(form.dateOfBirth)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Height (cm)</div>
                          <div className="np-summary-value">{show(form.heightCm)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Body weight (kg)</div>
                          <div className="np-summary-value">{show(form.bodyWeightKg)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Waist circumference (cm)</div>
                          <div className="np-summary-value">{show(form.waistCm)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Hip circumference (cm)</div>
                          <div className="np-summary-value">{show(form.hipCm)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Neck circumference (cm)</div>
                          <div className="np-summary-value">{show(form.neckCm)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Life stage</div>
                          <div className="np-summary-value">{show(form.lifeStage)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Mother tongue</div>
                          <div className="np-summary-value">{show(form.motherTongue)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Marital status</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.maritalStatus)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Motivation readiness</div>
                          <div className="np-summary-value">{show(form.motivationReadiness)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Previous diet attempts</div>
                          <div className="np-summary-value">{show(form.previousDietAttempts)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Support system available</div>
                          <div className="np-summary-value">{showBool(form.supportSystemAvailable)}</div>
                        </div>
                        <div className="np-summary-item">
                          <div className="np-summary-label">Technology comfort level</div>
                          <div className="np-summary-value">{show(form.technologyComfortLevel)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="np-summary-section">
                      <h4>Food preferences</h4>
                      <div className="np-summary-grid">
                        {show(preferences.typesOfDiet) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Types of diet</div>
                            <div className="np-summary-value">{show(preferences.typesOfDiet)}</div>
                        </div>
                        )}
                        {show(preferences.typesOfDietNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Types of diet notes</div>
                            <div className="np-summary-value">{show(preferences.typesOfDietNotes)}</div>
                          </div>
                        )}
                        {show(preferences.favoriteFood) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Favorite food</div>
                            <div className="np-summary-value">{show(preferences.favoriteFood)}</div>
                        </div>
                        )}
                        {show(preferences.dislikedFood) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Disliked food</div>
                            <div className="np-summary-value">{show(preferences.dislikedFood)}</div>
                        </div>
                        )}
                        {show(preferences.allergies) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Allergies</div>
                            <div className="np-summary-value">{show(preferences.allergies)}</div>
                        </div>
                        )}
                        {show(preferences.allergiesNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Allergies notes</div>
                            <div className="np-summary-value">{show(preferences.allergiesNotes)}</div>
                          </div>
                        )}
                        {show(preferences.foodIntolerances) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Food intolerances</div>
                            <div className="np-summary-value">{show(preferences.foodIntolerances)}</div>
                        </div>
                        )}
                        {show(preferences.foodIntolerancesNotes) && (
                        <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Food intolerances notes</div>
                            <div className="np-summary-value">{show(preferences.foodIntolerancesNotes)}</div>
                        </div>
                        )}
                        {show(preferences.nutritionalDeficiencies) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Nutritional deficiencies</div>
                            <div className="np-summary-value">{show(preferences.nutritionalDeficiencies)}</div>
                          </div>
                        )}
                        {show(preferences.nutritionalDeficienciesNotes) && (
                        <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Nutritional deficiencies notes</div>
                            <div className="np-summary-value">{show(preferences.nutritionalDeficienciesNotes)}</div>
                        </div>
                        )}
                        {show(preferences.waterIntake) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Water intake</div>
                            <div className="np-summary-value">{show(preferences.waterIntake)}</div>
                        </div>
                        )}
                        {show(preferences.otherInformation) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Other information</div>
                            <div className="np-summary-value">{show(preferences.otherInformation)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="np-summary-section">
                      <h4>Medical history</h4>
                      <div className="np-summary-grid">
                        {show(medicalHistory.diseases) && (
                        <div className="np-summary-item">
                            <div className="np-summary-label">Diseases</div>
                            <div className="np-summary-value">{show(medicalHistory.diseases)}</div>
                        </div>
                        )}
                        {medicalHistory.diseases === 'other' && show(medicalHistory.diseasesNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Diseases notes</div>
                            <div className="np-summary-value">{show(medicalHistory.diseasesNotes)}</div>
                          </div>
                        )}
                        {show(medicalHistory.medication) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Medication</div>
                            <div className="np-summary-value">{show(medicalHistory.medication)}</div>
                          </div>
                        )}
                        {medicalHistory.medication === 'other' && show(medicalHistory.medicationNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Medication details</div>
                            <div className="np-summary-value">{show(medicalHistory.medicationNotes)}</div>
                          </div>
                        )}
                        {show(medicalHistory.personalHistory) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Personal history</div>
                            <div className="np-summary-value">{show(medicalHistory.personalHistory)}</div>
                          </div>
                        )}
                        {medicalHistory.personalHistory === 'other' && show(medicalHistory.personalHistoryNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Personal history details</div>
                            <div className="np-summary-value">{show(medicalHistory.personalHistoryNotes)}</div>
                          </div>
                        )}
                        {show(medicalHistory.familyHistory) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Family history</div>
                            <div className="np-summary-value">{show(medicalHistory.familyHistory)}</div>
                          </div>
                        )}
                        {medicalHistory.familyHistory === 'other' && show(medicalHistory.familyHistoryNotes) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Family history details</div>
                            <div className="np-summary-value">{show(medicalHistory.familyHistoryNotes)}</div>
                          </div>
                        )}
                        {show(medicalHistory.otherInformation) && (
                          <div className="np-summary-item np-summary-item--full">
                            <div className="np-summary-label">Other information</div>
                            <div className="np-summary-value">{show(medicalHistory.otherInformation)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="np-summary-section">
                      <h4>Lifestyle</h4>
                      <div className="np-summary-grid">
                        {show((form.humanContextJson as any)?.activityLevel) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Activity level</div>
                            <div className="np-summary-value">
                              {(() => {
                                const level = (form.humanContextJson as any)?.activityLevel;
                                if (level === 'high') return 'Very Active';
                                return show(level);
                              })()}
                            </div>
                          </div>
                        )}
                        {show((form.humanContextJson as any)?.sleepQuality) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Sleep quality</div>
                            <div className="np-summary-value">{show((form.humanContextJson as any)?.sleepQuality)}</div>
                          </div>
                        )}
                        {show((form.humanContextJson as any)?.sleepPatterns) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Sleep patterns</div>
                            <div className="np-summary-value">{show((form.humanContextJson as any)?.sleepPatterns)}</div>
                          </div>
                        )}
                        {show((form.humanContextJson as any)?.cultureIdentity) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Culture / identity</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.cultureIdentity)}</div>
                        </div>
                        )}
                        {showList((form.humanContextJson as any)?.religiousRestrictions) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Religious restrictions</div>
                          <div className="np-summary-value">{showList((form.humanContextJson as any)?.religiousRestrictions)}</div>
                        </div>
                        )}
                        {show((form.humanContextJson as any)?.workSchedule) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Work schedule</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.workSchedule)}</div>
                        </div>
                        )}
                        {show((form.humanContextJson as any)?.stressLevels) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Stress level</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.stressLevels)}</div>
                        </div>
                        )}
                        {show((form.humanContextJson as any)?.familyStructure) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Family structure</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.familyStructure)}</div>
                        </div>
                        )}
                        {show((form.humanContextJson as any)?.cookingResponsibility) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Cooking responsibility</div>
                          <div className="np-summary-value">{show((form.humanContextJson as any)?.cookingResponsibility)}</div>
                        </div>
                        )}
                        {show((form.humanContextJson as any)?.smokingStatus) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Smoking status</div>
                            <div className="np-summary-value">{show((form.humanContextJson as any)?.smokingStatus)}</div>
                          </div>
                        )}
                        {show((form.humanContextJson as any)?.alcoholConsumption) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Alcohol consumption</div>
                            <div className="np-summary-value">{show((form.humanContextJson as any)?.alcoholConsumption)}</div>
                          </div>
                        )}
                        {show((form.humanContextJson as any)?.sleepPatterns) && (
                          <div className="np-summary-item">
                            <div className="np-summary-label">Sleep patterns</div>
                            <div className="np-summary-value">{show((form.humanContextJson as any)?.sleepPatterns)}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="np-summary-section">
                      <h4>Constraints</h4>
                      <div className="np-summary-grid">
                            {(() => {
                          const b = (form.constraintsJson as any)?.budget || null;
                          const budgetValue = b && b.monthly_budget !== undefined && b.monthly_budget !== null 
                            ? `${b.monthly_budget} ${b.currency || ''}`.trim() 
                            : null;
                          return budgetValue ? (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Monthly budget</div>
                              <div className="np-summary-value">{budgetValue}</div>
                          </div>
                          ) : null;
                        })()}
                        {show((form.constraintsJson as any)?.schedule?.work_hours) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Work hours</div>
                          <div className="np-summary-value">{show((form.constraintsJson as any)?.schedule?.work_hours)}</div>
                        </div>
                        )}
                        <div className="np-summary-item">
                          <div className="np-summary-label">Meal prep time (min)</div>
                          <div className="np-summary-value">{show((form.constraintsJson as any)?.schedule?.meal_prep_time_min)}</div>
                        </div>
                        <div className="np-summary-item np-summary-item--full">
                          <div className="np-summary-label">Won't do</div>
                          <div className="np-summary-value">{showList((form.constraintsJson as any)?.wontDo)}</div>
                        </div>
                        {(() => {
                          const cooking = (form.constraintsJson as any)?.cooking || null;
                          if (cooking) {
                            const cookingItems: string[] = [];
                            if (cooking.has_stove) cookingItems.push('Stove');
                            if (cooking.has_microwave) cookingItems.push('Microwave');
                            if (cooking.has_refrigerator) cookingItems.push('Refrigerator');
                            if (cooking.has_oven) cookingItems.push('Oven');
                            if (cookingItems.length > 0) {
                              return (
                                <div className="np-summary-item np-summary-item--full">
                                  <div className="np-summary-label">Cooking facilities</div>
                                  <div className="np-summary-value">{cookingItems.join(', ')}</div>
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    <div className="np-summary-section">
                      <h4>Meal pattern</h4>
                      <div className="np-summary-grid">
                        {show((form.mealPatternJson as any)?.typicalMealsPerDay) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Meals per day</div>
                          <div className="np-summary-value">{show((form.mealPatternJson as any)?.typicalMealsPerDay)}</div>
                        </div>
                        )}
                        {show((form.mealPatternJson as any)?.snackFrequency) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Snack frequency</div>
                          <div className="np-summary-value">{show((form.mealPatternJson as any)?.snackFrequency)}</div>
                        </div>
                        )}
                        {showBool((form.mealPatternJson as any)?.lateNightEating) !== null && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Late-night eating</div>
                          <div className="np-summary-value">{showBool((form.mealPatternJson as any)?.lateNightEating)}</div>
                        </div>
                        )}
                        {show((form.mealPatternJson as any)?.weekendPatterns) && (
                        <div className="np-summary-item">
                          <div className="np-summary-label">Weekend pattern</div>
                          <div className="np-summary-value">{show((form.mealPatternJson as any)?.weekendPatterns)}</div>
                        </div>
                        )}
                        {(() => {
                          const mt = (form.mealPatternJson as any)?.mealTimes || null;
                          if (!mt) return null;
                          const keys = Object.keys(mt).filter((k) => mt[k]);
                          if (keys.length === 0) return null;
                          
                          // Format time from HH:MM to 12-hour format with AM/PM
                          const formatTime = (timeStr: string): string => {
                            if (!timeStr || !timeStr.includes(':')) return timeStr;
                            const [hh, mm] = timeStr.split(':');
                            const hour24 = parseInt(hh, 10);
                            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                            const ampm = hour24 < 12 ? 'AM' : 'PM';
                            return `${hour12}:${mm} ${ampm}`;
                          };
                          
                          // Get meal type label
                          const getMealTypeLabel = (mealType: string): string => {
                            const mealTypeObj = MEAL_TYPES.find(m => m.value === mealType);
                            return mealTypeObj ? mealTypeObj.label : mealType.charAt(0).toUpperCase() + mealType.slice(1);
                          };
                          
                          return (
                        <div className="np-summary-item np-summary-item--full">
                          <div className="np-summary-label">Typical meal times</div>
                          <div className="np-summary-value">
                                {keys.map((k) => `${getMealTypeLabel(k)}: ${formatTime(mt[k])}`).join(' • ')}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Doctor and Patient Notes Section */}
              {(show(form.doctorNotes) || show(form.patientNotes)) && (
                <div className="np-summary-section" style={{ marginTop: '24px' }}>
                  <h4>Notes</h4>
                  <div className="np-summary-grid">
                    {show(form.doctorNotes) && (
                      <div className="np-summary-item np-summary-item--full">
                        <div className="np-summary-label">Doctor notes</div>
                        <div className="np-summary-value">{show(form.doctorNotes)}</div>
                      </div>
                    )}
                    {show(form.patientNotes) && (
                      <div className="np-summary-item np-summary-item--full">
                        <div className="np-summary-label">Patient notes</div>
                        <div className="np-summary-value">{show(form.patientNotes)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Editable Notes Fields in Review Step */}
              <div className="np-summary-section" style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                <h4>Additional Notes</h4>
                <div className="np-grid">
                  <div className="np-field np-field--full">
                    <label>Doctor notes</label>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                      <textarea
                        rows={4}
                        placeholder="Doctor's clinical notes, observations, or recommendations..."
                        value={form.doctorNotes || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, doctorNotes: e.target.value }))}
                        style={getParsedFieldInputStyle(getSourceForFormField(parseResult, 'doctorNotes', form.doctorNotes))}
                        className={getParsedFieldInputClassName(getSourceForFormField(parseResult, 'doctorNotes', form.doctorNotes)) || undefined}
                      />
                      {getSourceForFormField(parseResult, 'doctorNotes', form.doctorNotes) === 'edited' && (
                        <button type="button" onClick={() => setForm((prev) => ({ ...prev, doctorNotes: String(getParsedValueForFormKey(parseResult, 'doctorNotes') ?? '') }))} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start' }}>Revert</button>
                      )}
                    </div>
                  </div>
                  <div className="np-field np-field--full">
                    <label>Patient notes</label>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                      <textarea
                        rows={4}
                        placeholder="Patient's own notes, concerns, or preferences..."
                        value={form.patientNotes || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, patientNotes: e.target.value }))}
                        style={getParsedFieldInputStyle(getSourceForFormField(parseResult, 'patientNotes', form.patientNotes))}
                        className={getParsedFieldInputClassName(getSourceForFormField(parseResult, 'patientNotes', form.patientNotes)) || undefined}
                      />
                      {getSourceForFormField(parseResult, 'patientNotes', form.patientNotes) === 'edited' && (
                        <button type="button" onClick={() => setForm((prev) => ({ ...prev, patientNotes: String(getParsedValueForFormKey(parseResult, 'patientNotes') ?? '') }))} title="Revert to original value" style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start' }}>Revert</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // If profile exists and not in edit mode, show view mode
  if (!isEditMode && profile) {
    // Format enum values to human-readable text
    const formatEnumValue = (value: string | null | undefined): string | null => {
      if (!value || typeof value !== 'string') return null;
      
      const enumMap: Record<string, string> = {
        // Diet types
        'none': 'None',
        'vegetarian': 'Vegetarian',
        'non_vegetarian': 'Non Vegetarian',
        'vegan': 'Vegan',
        'keto': 'Keto',
        'paleo': 'Paleo',
        'mediterranean': 'Mediterranean',
        'low_carb': 'Low Carb',
        'low_fat': 'Low Fat',
        
        // Allergies
        'peanuts': 'Peanuts',
        'tree_nuts': 'Tree Nuts',
        'dairy': 'Dairy',
        'eggs': 'Eggs',
        'fish': 'Fish',
        'shellfish': 'Shellfish',
        'soy': 'Soy',
        'wheat': 'Wheat',
        
        // Food intolerances
        'lactose': 'Lactose',
        'gluten': 'Gluten',
        'fructose': 'Fructose',
        'histamine': 'Histamine',
        
        // Nutritional deficiencies
        'vitamin_d': 'Vitamin D',
        'vitamin_b12': 'Vitamin B12',
        'iron': 'Iron',
        'calcium': 'Calcium',
        'vitamin_c': 'Vitamin C',
        'zinc': 'Zinc',
        'magnesium': 'Magnesium',
        'folate': 'Folate',
        
        // Water intake
        'less_than_2_liters': 'Less than 2 liters/day',
        '2_4_liters': '2-4 liters/day',
        'more_than_4_liters': 'More than 4 liters/day',
        
        // Diseases
        'diabetes': 'Diabetes',
        'hypertension': 'Hypertension',
        'heart_disease': 'Heart Disease',
        'thyroid': 'Thyroid',
        'pcos': 'PCOS',
        'ibs': 'IBS',
        'crohns': "Crohn's Disease",
        'celiac': 'Celiac Disease',
        
        // Medication
        'metformin': 'Metformin',
        'aspirin': 'Aspirin',
        'thyroid_medication': 'Thyroid Medication',
        'blood_pressure_medication': 'Blood Pressure Medication',
        
        // Personal/Family history
        'diabetes_family': 'Diabetes',
        'heart_disease_family': 'Heart Disease',
        'cancer_family': 'Cancer',
        
        // Activity level
        'sedentary': 'Sedentary',
        'light': 'Lightly Active',
        'moderate': 'Moderately Active',
        
        // Stress levels, Motivation readiness, Technology comfort (shared values)
        'low': 'Low',
        'medium': 'Medium',
        'high': 'High', // Note: For activity level 'high', it should be 'Very Active' but handled separately
        
        // Gender
        'male': 'Male',
        'female': 'Female',
        'other': 'Other',
        
        // Life stage
        'adult': 'Adult',
        'elderly': 'Elderly',
        'pregnant': 'Pregnant',
        'lactating': 'Lactating',
        
        // Marital status
        'single': 'Single',
        'married': 'Married',
        'divorced': 'Divorced',
        'widowed': 'Widowed',
        'separated': 'Separated',
        
        // Captured via
        'doctor_visit': 'Doctor Visit',
        'tele_consult': 'Tele Consult',
        'chat': 'Chat',
        'onboarding': 'Onboarding',
        'self_report': 'Self Report',
      };
      
        return enumMap[value.toLowerCase()] || value.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const show = (v: any) => {
      if (v === null || v === undefined) return null;
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (!trimmed) return null;
        return formatEnumValue(trimmed) || trimmed;
      }
      return String(v);
    };

    const renderValue = (v: any) => (show(v) != null ? show(v) : '-');

    const p = profile;
    const h = p.heightCm != null ? Number(p.heightCm) : 0;
    const w = p.bodyWeightKg != null ? Number(p.bodyWeightKg) : 0;
    const bmi = h > 0 && w > 0 ? (w / ((h / 100) ** 2)).toFixed(1) : null;
    const pregnantLactating =
      p.isPregnant === true || p.isLactating === true
        ? 'Yes'
        : p.isPregnant === false && p.isLactating === false
          ? 'No'
          : null;
    const diseasesStr = p.medicalHistoryJson?.diseases ?? '';
    const diseaseTags = diseasesStr ? diseasesStr.split(/\s*,\s*/).filter(Boolean) : [];
    const personalAndFamily = [
      p.medicalHistoryJson?.personalHistory,
      p.medicalHistoryJson?.familyHistory,
    ].filter(Boolean).join('; ') || null;

    return (
            <div className="nutrition-profile-view">
        {error && <div className="error-message">{error}</div>}

        <div className="section-card">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '24px',
            paddingBottom: '20px',
            borderBottom: '2px solid #e2e8f0'
          }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: '24px', 
              fontWeight: '700', 
              color: '#111827' 
            }}>
              Nutrition Profile
            </h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button 
                type="button" 
                className="primary-button" 
                onClick={async () => {
                // Load current nutrition state to ensure we use latest vitals when editing
                try {
                  const currentState = await nutritionProfileService.getCurrentStateForPatient(patientId);
                  if (currentState) {
                    const snapshot = currentState.profile_snapshot;
                    const anthropometrics = currentState.anthropometrics;
                    
                    // Update form with latest vitals (prefer vitals over snapshot for measurements)
                    setForm((prev) => ({
                      ...prev,
                      // Use latest vitals for anthropometrics, fallback to snapshot
                      heightCm: anthropometrics.height_cm || snapshot?.height_cm || prev.heightCm,
                      bodyWeightKg: anthropometrics.body_weight_kg || snapshot?.body_weight_kg || prev.bodyWeightKg,
                      waistCm: anthropometrics.waist_cm || snapshot?.waist_cm || prev.waistCm,
                      hipCm: anthropometrics.hip_cm || snapshot?.hip_cm || prev.hipCm,
                    }));
                  }
                } catch (stateError: any) {
                  console.warn('Failed to load current nutrition state for edit:', stateError);
                  // Continue anyway - form already has profile data
                }
                setIsEditMode(true);
                setActiveStep('import_or_manual');
              }}
                style={{
                  padding: '10px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>Edit Profile</span>
              </button>
            </div>
          </div>

          {profile && (
            <div className="profile-meta-info" style={{ 
              marginBottom: '24px',
              padding: '16px 20px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px 32px',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Version</span>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#111827', 
                  fontWeight: '600',
                  backgroundColor: '#ffffff',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>{profile.profileVersion}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Captured on</span>
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{profile.captureDate}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Captured via</span>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#111827', 
                  fontWeight: '500',
                  textTransform: 'capitalize'
                }}>{formatEnumValue(profile.capturedViaEnum) || profile.capturedViaEnum?.replace('_', ' ')}</span>
              </div>
            </div>
          )}

          <div className="profile-collapse-cards">
            {/* Basics */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('basics')}
                aria-expanded={profileSectionsOpen.basics}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <User size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Basics</span>
                  <span className="profile-collapse-card-subtitle">Personal vitals and demographics</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.basics ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.basics && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid profile-at-creation-grid-basics">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">GENDER</span>
                      <span className="profile-at-creation-value">{renderValue(p.gender)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">DATE OF BIRTH</span>
                      <span className="profile-at-creation-value">
                        {p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString() : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">HEIGHT (CM)</span>
                      <span className="profile-at-creation-value">{renderValue(p.heightCm)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">BODY WEIGHT (KG)</span>
                      <span className="profile-at-creation-value">
                        {renderValue(p.bodyWeightKg)}
                        {bmi != null && <span className="profile-at-creation-bmi"> (BMI: {bmi})</span>}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">WAIST (CM)</span>
                      <span className="profile-at-creation-value">{renderValue(p.waistCm)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">HIP (CM)</span>
                      <span className="profile-at-creation-value">{renderValue(p.hipCm)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">NECK (CM)</span>
                      <span className="profile-at-creation-value">{renderValue(p.neckCm)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">LIFE STAGE</span>
                      <span className="profile-at-creation-value">{renderValue(p.lifeStage)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">PREGNANT/LACTATING</span>
                      <span className="profile-at-creation-value">{pregnantLactating ?? '-'}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MOTHER TONGUE</span>
                      <span className="profile-at-creation-value">{renderValue(p.motherTongue)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">ACTIVITY LEVEL</span>
                      <span className="profile-at-creation-value">{renderValue(p.activityLevel)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MOTIVATION READINESS</span>
                      <span className="profile-at-creation-value">{renderValue(p.motivationReadiness)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">PREVIOUS ATTEMPTS</span>
                      <span className="profile-at-creation-value">{renderValue(p.previousDietAttempts)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">SUPPORT AVAILABLE</span>
                      <span className="profile-at-creation-value">{renderValue(p.supportSystemAvailable)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">TECH COMFORT</span>
                      <span className="profile-at-creation-value">{renderValue(p.technologyComfortLevel)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Food Preferences */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('foodPreferences')}
                aria-expanded={profileSectionsOpen.foodPreferences}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <Utensils size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Food Preferences</span>
                  <span className="profile-collapse-card-subtitle">Dietary habits and restrictions</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.foodPreferences ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.foodPreferences && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">TYPE OF DIET</span>
                      <span className="profile-at-creation-value profile-at-creation-diet-value">
                        <span className="profile-at-creation-diet-dot" />
                        {p.preferencesJson?.typesOfDiet ? String(p.preferencesJson.typesOfDiet) : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">DIET NOTES</span>
                      <span className={`profile-at-creation-value ${!p.preferencesJson?.typesOfDietNotes ? 'profile-at-creation-placeholder' : ''}`}>
                        {p.preferencesJson?.typesOfDietNotes ?? 'None provided'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">FAVORITE FOODS</span>
                      <span className="profile-at-creation-value">{renderValue(p.preferencesJson?.favoriteFood)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">DISLIKED FOODS</span>
                      <span className="profile-at-creation-value">{renderValue(p.preferencesJson?.dislikedFood)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">ALLERGIES</span>
                      <span className="profile-at-creation-value">
                        {p.preferencesJson?.allergies != null && p.preferencesJson.allergies !== '' ? p.preferencesJson.allergies : 'None'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">FOOD INTOLERANCES</span>
                      <span className="profile-at-creation-value">{renderValue(p.preferencesJson?.foodIntolerances)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">NUTRITIONAL DEFICIENCIES</span>
                      <span className="profile-at-creation-value">{renderValue(p.preferencesJson?.nutritionalDeficiencies)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">WATER INTAKE</span>
                      <span className="profile-at-creation-value">{renderValue(p.preferencesJson?.waterIntake)}</span>
                    </div>
                    <div className="profile-at-creation-field profile-at-creation-field-full">
                      <span className="profile-at-creation-label">OTHER PREFERENCES</span>
                      <div className="profile-at-creation-other-pref">{renderValue(p.preferencesJson?.otherInformation)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Medical History */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('medicalHistory')}
                aria-expanded={profileSectionsOpen.medicalHistory}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <Stethoscope size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Medical History</span>
                  <span className="profile-collapse-card-subtitle">Clinical profile and health history</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.medicalHistory ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.medicalHistory && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid profile-at-creation-grid-medical">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">DISEASES</span>
                      <span className="profile-at-creation-value">
                        {diseaseTags.length > 0
                          ? diseaseTags.map((d, i) => (
                              <span key={i} className="profile-at-creation-pill">{d.trim()}</span>
                            ))
                          : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">DISEASE NOTES</span>
                      <span className="profile-at-creation-value">{renderValue(p.medicalHistoryJson?.diseasesNotes)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MEDICATION</span>
                      <span className={`profile-at-creation-value ${!p.medicalHistoryJson?.medication ? 'profile-at-creation-placeholder' : ''}`}>
                        {p.medicalHistoryJson?.medication != null && p.medicalHistoryJson.medication !== '' ? p.medicalHistoryJson.medication : 'None'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MEDICATION NOTES</span>
                      <span className="profile-at-creation-value">{renderValue(p.medicalHistoryJson?.medicationNotes)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">PERSONAL & FAMILY HISTORY</span>
                      <span className="profile-at-creation-value">{personalAndFamily ?? '-'}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">OTHER MEDICAL INFO</span>
                      <span className="profile-at-creation-value">{renderValue(p.medicalHistoryJson?.otherInformation)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Lifestyle */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('humanContext')}
                aria-expanded={profileSectionsOpen.humanContext}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <UserCog size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Lifestyle</span>
                  <span className="profile-collapse-card-subtitle">Lifestyle and environmental factors</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.humanContext ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.humanContext && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid profile-at-creation-grid-human">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">ACTIVITY LEVEL</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.activityLevel)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">CULTURAL IDENTITY</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.cultureIdentity)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">RELIGIOUS RESTRICTIONS</span>
                      <span className="profile-at-creation-value">
                        {p.humanContextJson?.religiousRestrictions?.length
                          ? p.humanContextJson.religiousRestrictions.join(', ')
                          : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">FOOD RELATIONSHIP</span>
                      <span className="profile-at-creation-value">-</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">SLEEP PATTERNS</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.sleepPatterns)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">STRESS LEVELS</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.stressLevels)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">WORK SCHEDULE</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.workSchedule)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">FAMILY STRUCTURE</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.familyStructure)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">COOKING RESP.</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.cookingResponsibility)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">SLEEP QUALITY</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.sleepQuality)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">SMOKING STATUS</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.smokingStatus)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">ALCOHOL CONSUMPTION</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.alcoholConsumption)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MARITAL STATUS</span>
                      <span className="profile-at-creation-value">{renderValue(p.humanContextJson?.maritalStatus)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Constraints */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('constraints')}
                aria-expanded={profileSectionsOpen.constraints}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <Settings size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Constraints</span>
                  <span className="profile-collapse-card-subtitle">Cooking, budget and schedule</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.constraints ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.constraints && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">COOKING FACILITIES</span>
                      <span className="profile-at-creation-value">
                        {p.constraintsJson?.cooking
                          ? [
                              p.constraintsJson.cooking.has_stove ? 'Stove' : null,
                              p.constraintsJson.cooking.has_microwave ? 'Microwave' : null,
                              p.constraintsJson.cooking.has_refrigerator ? 'Refrigerator' : null,
                              p.constraintsJson.cooking.has_oven ? 'Oven' : null,
                            ]
                              .filter(Boolean)
                              .join(', ') || '-'
                          : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">COOKING SKILL LEVEL</span>
                      <span className="profile-at-creation-value">{renderValue(p.constraintsJson?.cooking?.cooking_skills_level)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MONTHLY BUDGET</span>
                      <span className="profile-at-creation-value">
                        {p.constraintsJson?.budget?.monthly_budget != null
                          ? `${p.constraintsJson.budget.monthly_budget} ${p.constraintsJson.budget?.currency ?? ''}`.trim()
                          : '-'}
                      </span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">WORK HOURS</span>
                      <span className="profile-at-creation-value">{renderValue(p.constraintsJson?.schedule?.work_hours)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">MEAL PREP TIME (MIN)</span>
                      <span className="profile-at-creation-value">{renderValue(p.constraintsJson?.schedule?.meal_prep_time_min)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">EATING OUT FREQUENCY</span>
                      <span className="profile-at-creation-value">{renderValue(p.constraintsJson?.schedule?.eatingOutFrequency)}</span>
                    </div>
                    <div className="profile-at-creation-field profile-at-creation-field-full">
                      <span className="profile-at-creation-label">WON'T DO</span>
                      <span className="profile-at-creation-value">
                        {p.constraintsJson?.wontDo?.length ? p.constraintsJson.wontDo.join(', ') : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Meal Pattern */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('mealPattern')}
                aria-expanded={profileSectionsOpen.mealPattern}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <CalendarClock size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Meal Pattern</span>
                  <span className="profile-collapse-card-subtitle">Typical meals and timing</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.mealPattern ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.mealPattern && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid">
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">TYPICAL MEALS PER DAY</span>
                      <span className="profile-at-creation-value">{renderValue(p.mealPatternJson?.typicalMealsPerDay)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">SNACK FREQUENCY</span>
                      <span className="profile-at-creation-value">{renderValue(p.mealPatternJson?.snackFrequency)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">LATE NIGHT EATING</span>
                      <span className="profile-at-creation-value">{renderValue(p.mealPatternJson?.lateNightEating)}</span>
                    </div>
                    <div className="profile-at-creation-field">
                      <span className="profile-at-creation-label">WEEKEND PATTERNS</span>
                      <span className="profile-at-creation-value">{renderValue(p.mealPatternJson?.weekendPatterns)}</span>
                    </div>
                    <div className="profile-at-creation-field profile-at-creation-field-full">
                      <span className="profile-at-creation-label">MEAL TIMES</span>
                      <span className="profile-at-creation-value">
                        {p.mealPatternJson?.mealTimes && Object.keys(p.mealPatternJson.mealTimes).length > 0
                          ? Object.entries(p.mealPatternJson.mealTimes)
                              .map(([meal, time]) => `${meal}: ${time}`)
                              .join(', ')
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="profile-collapse-card">
              <button
                type="button"
                className="profile-collapse-card-header"
                onClick={() => toggleProfileSection('notes')}
                aria-expanded={profileSectionsOpen.notes}
              >
                <span className="profile-collapse-card-icon-wrap">
                  <FileText size={18} />
                </span>
                <span className="profile-collapse-card-heading">
                  <span className="profile-collapse-card-title">Notes</span>
                  <span className="profile-collapse-card-subtitle">Doctor and patient notes</span>
                </span>
                <ChevronDown size={20} className={`profile-collapse-card-chevron ${profileSectionsOpen.notes ? 'profile-collapse-card-chevron-open' : ''}`} />
              </button>
              {profileSectionsOpen.notes && (
                <div className="profile-collapse-card-body">
                  <div className="profile-at-creation-grid">
                    <div className="profile-at-creation-field profile-at-creation-field-full">
                      <span className="profile-at-creation-label">DOCTOR NOTES</span>
                      <span className="profile-at-creation-value">{renderValue(p.doctorNotes)}</span>
                    </div>
                    <div className="profile-at-creation-field profile-at-creation-field-full">
                      <span className="profile-at-creation-label">PATIENT NOTES</span>
                      <span className="profile-at-creation-value">{renderValue(p.patientNotes)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          </div>
        );
  }

  // If no profile exists and not in edit mode, show create option
  if (!isEditMode && !profile) {
    return (
      <div className="nutrition-profile-empty">
        {error && <div className="error-message">{error}</div>}

        <div className="section-card">
          <div style={{ marginBottom: '20px' }}>
            <p className="np-muted" style={{ marginTop: 0, marginBottom: 0, fontSize: '1rem', color: '#6b7280', textAlign: 'center' }}>
              Capture comprehensive nutrition information including demographics, food preferences, medical history, and lifestyle factors to create a personalized nutrition plan.
            </p>
          </div>
          <div style={{ textAlign: 'center', paddingTop: '20px', borderTop: '1px solid #e5e7eb', paddingBottom: '20px' }}>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '0.95rem' }}>
              No current nutrition profile found
            </p>
            <button
              type="button"
              className="primary-button"
              onClick={async () => {
                // Load current nutrition state to pre-fill form with latest vitals
                try {
                  const currentState = await nutritionProfileService.getCurrentStateForPatient(patientId);
                  if (currentState) {
                    const snapshot = currentState.profile_snapshot;
                    const anthropometrics = currentState.anthropometrics;
                    const demographics = currentState.demographics;
                    
                    setForm((prev) => ({
                      ...prev,
                      userId: patientId,
                      capturedViaEnum: 'doctor_visit',
                      gender: snapshot?.gender || demographics.gender || prev.gender,
                      heightCm: anthropometrics.height_cm || snapshot?.height_cm || demographics.height_cm || prev.heightCm,
                      bodyWeightKg: anthropometrics.body_weight_kg || snapshot?.body_weight_kg || prev.bodyWeightKg,
                      waistCm: anthropometrics.waist_cm || snapshot?.waist_cm || prev.waistCm,
                      hipCm: anthropometrics.hip_cm || snapshot?.hip_cm || prev.hipCm,
                      neckCm: snapshot?.neck_cm || prev.neckCm,
                      dateOfBirth: snapshot?.date_of_birth || demographics.date_of_birth || prev.dateOfBirth,
                      lifeStage: snapshot?.life_stage || (demographics.date_of_birth ? deriveLifeStageFromDOB(demographics.date_of_birth) : prev.lifeStage),
                      isPregnant: snapshot?.is_pregnant !== undefined ? snapshot.is_pregnant : prev.isPregnant,
                      isLactating: snapshot?.is_lactating !== undefined ? snapshot.is_lactating : prev.isLactating,
                      motherTongue: prev.motherTongue, // Not in snapshot, keep existing
                      preferencesJson: prev.preferencesJson || snapshot?.preferences || emptyPreferences(),
                      humanContextJson: prev.humanContextJson, // Not in snapshot, keep existing
                      constraintsJson: snapshot?.constraints || prev.constraintsJson,
                      medicalHistoryJson: snapshot?.medical_history || prev.medicalHistoryJson,
                      mealPatternJson: snapshot?.meal_pattern || prev.mealPatternJson,
                      motivationReadiness: snapshot?.motivation_readiness || prev.motivationReadiness,
                      previousDietAttempts: snapshot?.previous_diet_attempts ?? prev.previousDietAttempts,
                      supportSystemAvailable: snapshot?.support_system_available !== undefined ? snapshot.support_system_available : prev.supportSystemAvailable,
                      technologyComfortLevel: snapshot?.technology_comfort_level || prev.technologyComfortLevel,
                      doctorNotes: snapshot?.doctor_notes || prev.doctorNotes,
                      patientNotes: snapshot?.patient_notes || prev.patientNotes,
                    }));
                  }
                } catch (stateError: any) {
                  console.warn('Failed to load current nutrition state for create:', stateError);
                  // Continue anyway - form will start empty
                }
                setIsEditMode(true);
                setActiveStep('import_or_manual');
              }}
            >
              Create Nutrition Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show wizard when in edit mode (creating or editing)
  return (
    <div className="nutrition-profile-wizard">
      {error && <div className="error-message">{error}</div>}

      <div className="np-stepper">
        <div className="np-stepper-header">
          {steps.map((s, idx) => {
            const isActive = s.id === activeStep;
            const isDone = idx < stepIndex;
            const hasData = stepsWithLoadedData.has(s.id);
            return (
              <button
                key={s.id}
                type="button"
                className={`np-stepper-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${hasData ? 'has-data' : ''}`}
                onClick={() => setActiveStep(s.id)}
              >
                <span className="np-stepper-index">{idx + 1}</span>
                <span className="np-stepper-title">{s.title}</span>
              </button>
            );
          })}
        </div>

        <div className="np-stepper-body">
          {!(activeStep === 'import_or_manual' && showImportArea) && (
            <div className="np-step-title">
              <h2>{steps[stepIndex]?.title}</h2>
              <p className="np-muted">{steps[stepIndex]?.subtitle}</p>
            </div>
          )}

          {renderStep()}

          <div className="np-actions" style={stepIndex === 0 ? { justifyContent: 'space-between' } : {}}>
            <button
              type="button"
              onClick={handleCancel}
              className="secondary-button"
            >
              Cancel
            </button>
            <div style={{ display: 'flex', gap: '10px' }}>
              {stepIndex > 0 && (
                <button type="button" className="primary-button" onClick={goBack}>
              Back
            </button>
              )}

            {activeStep === 'import_or_manual' ? null : activeStep !== 'review' ? (
              <button type="button" className="primary-button" onClick={goNext}>
                Next
              </button>
            ) : (
              <button type="button" className="primary-button" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : profile ? 'Save (new version)' : 'Create profile'}
              </button>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview extracted details modal */}
      {showExtractedPreviewModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="extracted-preview-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
          onClick={(e) => e.target === e.currentTarget && setShowExtractedPreviewModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              maxWidth: parseResultsByDocument && parseResultsByDocument.length > 1 ? '680px' : '560px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="extracted-preview-title" style={{ margin: 0, padding: '20px 24px 12px', fontSize: '1.25rem', fontWeight: 600, color: '#111827', borderBottom: '1px solid #e5e7eb' }}>
              Extracted details
            </h2>
            <p style={{ margin: 0, padding: '8px 24px 16px', fontSize: '0.875rem', color: '#6b7280' }}>
              {parseResultsByDocument && parseResultsByDocument.length > 1
                ? 'Values from multiple documents. For overlapping fields, select the document to use with the checkbox (default: first). Then click &quot;Load into form&quot;.'
                : 'Review the values below. Click &quot;Load into form&quot; to prefill the Basics and other steps.'}
            </p>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
              {(() => {
                const formatVal = (v: unknown): string => {
                  if (v === null || v === undefined) return '';
                  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
                  if (typeof v === 'number') return String(v);
                  return String(v);
                };

                if (parseResultsByDocument && parseResultsByDocument.length > 1) {
                  const allKeys = new Set<string>();
                  parseResultsByDocument.forEach(({ extracted }) => Object.keys(extracted || {}).forEach((k) => allKeys.add(k)));
                  const keyToDocIndices = new Map<string, number[]>();
                  allKeys.forEach((key) => {
                    const indices: number[] = [];
                    parseResultsByDocument.forEach(({ extracted }, idx) => {
                      const v = extracted && (extracted as Record<string, unknown>)[key];
                      if (v !== undefined && v !== null && v !== '') indices.push(idx);
                    });
                    if (indices.length > 0) keyToDocIndices.set(key, indices);
                  });
                  const entries = Array.from(keyToDocIndices.entries()).sort(([a], [b]) => a.localeCompare(b));
                  if (entries.length === 0) {
                    return <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No extracted values to show.</p>;
                  }
                  const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
                  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151', verticalAlign: 'top' };
                  const tdStyle: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#111827', wordBreak: 'break-word' };
                  return (
                    <table style={tableStyle} role="grid" aria-label="Extracted values by document">
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, minWidth: '160px' }}>Field</th>
                          {parseResultsByDocument.map((doc, idx) => (
                            <th key={idx} style={{ ...thStyle, maxWidth: '180px' }} title={doc.filename}>
                              {doc.filename.length > 24 ? doc.filename.slice(0, 21) + '…' : doc.filename}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map(([key, docIndices]) => {
                          const selectedIndex = selectedDocumentPerField[key] ?? 0;
                          const effectiveIndex = docIndices.includes(selectedIndex) ? selectedIndex : docIndices[0];
                          const selectDoc = (docIndex: number) => {
                            setSelectedDocumentPerField((prev) => {
                              const nextSel = { ...prev, [key]: docIndex };
                              setParseResult(mergeExtractions(parseResultsByDocument!, nextSel));
                              return nextSel;
                            });
                          };
                          return (
                            <tr key={key}>
                              <td style={{ ...tdStyle, fontWeight: 500, color: '#374151' }}>
                                {EXTRACTED_FIELD_LABELS[key] || key.replace(/_/g, ' ')}
                              </td>
                              {parseResultsByDocument.map((doc, idx) => {
                                const value = doc.extracted && (doc.extracted as Record<string, unknown>)[key];
                                const hasValue = value !== undefined && value !== null && value !== '';
                                const isSelected = effectiveIndex === idx;
                                return (
                                  <td key={idx} style={tdStyle}>
                                    {hasValue ? (
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => selectDoc(idx)}
                                          style={{ margin: 0, flexShrink: 0 }}
                                          aria-label={`Use value from ${doc.filename} for ${EXTRACTED_FIELD_LABELS[key] || key}`}
                                        />
                                        <span>{formatVal(value)}</span>
                                      </label>
                                    ) : (
                                      <span style={{ color: '#9ca3af' }}>—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                }

                const extracted = parseResult || {};
                const singleEntries = Object.entries(extracted).filter(([, v]) => v !== null && v !== undefined && v !== '');
                if (singleEntries.length === 0) {
                  return <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No extracted values to show.</p>;
                }
                return (
                  <dl style={{ margin: 0, display: 'grid', gap: '12px 16px' }}>
                    {singleEntries.map(([key, value]) => (
                      <div key={key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '8px', alignItems: 'baseline' }}>
                        <dt style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                          {EXTRACTED_FIELD_LABELS[key] || key.replace(/_/g, ' ')}
                        </dt>
                        <dd style={{ margin: 0, fontSize: '0.875rem', color: '#111827', wordBreak: 'break-word' }}>
                          {formatVal(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                );
              })()}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', padding: '16px 24px 24px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="secondary-button" onClick={() => setShowExtractedPreviewModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  applyParsedToForm(parseResult || {});
                  setShowExtractedPreviewModal(false);
                }}
              >
                Load into form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientNutritionProfileTab;
